import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { mloPhotoUrl } from "@/lib/photo";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import {
  LogOut,
  Users,
  MousePointerClick,
  TrendingUp,
  Loader2,
  RefreshCw,
  Lock,
  Search,
  Download,
  FileText,
  Image as ImageIcon,
  FolderOpen,
  X,
  Eye,
  EyeOff,
  Send,
  Mail,
  CheckCircle2,
  UserCheck,
  ClipboardList,
  Clock,
  Copy,
  Link2,
  ExternalLink,
  QrCode,
  Megaphone,
  BellRing,
  AlertTriangle,
  MailCheck,
  Archive,
  ArchiveRestore,
  Trash2,
} from "lucide-react";
import { ASSETS } from "@/lib/constants";
import BDMTeamPanel from "@/components/admin/BDMTeamPanel";
import BDMShelfPanel from "@/components/admin/BDMShelfPanel";
import BDMFilingsPanel from "@/components/admin/BDMFilingsPanel";
import ShelfAnnounceCard from "@/components/admin/ShelfAnnounceCard";
import AssetLeaderboardPanel from "@/components/admin/AssetLeaderboardPanel";
import PartnerActivationCard from "@/components/admin/PartnerActivationCard";
import ApplicantPipelineCard from "@/components/ApplicantPipelineCard";
import JourneyPanel, { AttributionProbeCard } from "@/components/admin/JourneyPanel";
import CommandCenter from "@/components/admin/CommandCenter";
import CommandPalette from "@/components/admin/CommandPalette";
import AttributionsCard from "@/components/admin/AttributionsCard";
import BrokeragesCard from "@/components/admin/BrokeragesCard";
import AnnualCheckPanel from "@/components/admin/AnnualCheckPanel";
import WelcomeVideoCard from "@/components/WelcomeVideoCard";
import { CORE_LINKS, DOWNLOAD_LINKS, TOOLKIT_GROUPS, toolkitKeyForGroup } from "@/lib/links";
import { CommsPanel } from "@/components/dashboard/CommsPanel";
import { GoogleSignInButton, AuthDivider } from "@/components/GoogleSignInButton";
import { clearGoogleSession } from "@/lib/googleSession";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "grc_admin_token";

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Global 401 handler — any admin API call that returns 401 clears the token
// and drops the user back to the sign-in screen with a friendly toast. Prevents
// the raw "Token expired" backend string from leaking into form error surfaces
// after a long session.
let sessionExpiredNotified = false;
function installGlobalAdmin401Handler() {
  const isAdminUrl = (url) => typeof url === "string" && url.includes("/api/admin/");
  const handler = (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && isAdminUrl(url) && localStorage.getItem(TOKEN_KEY)) {
      localStorage.removeItem(TOKEN_KEY);
      if (!sessionExpiredNotified) {
        sessionExpiredNotified = true;
        try {
          toast.error("Session expired — please sign in again.");
        } catch { /* toast not mounted yet — reload will still handle it */ }
        // Reload after a beat so the toast renders. This will show the login form.
        setTimeout(() => {
          sessionExpiredNotified = false;
          window.location.reload();
        }, 1200);
      }
    }
    return Promise.reject(error);
  };
  api.interceptors.response.use((r) => r, handler);
  // Also attach to the default axios instance since many places call axios directly.
  axios.interceptors.response.use((r) => r, handler);
}
installGlobalAdmin401Handler();

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function toCSV(rows, headers) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = headers.map((h) => esc(h.label)).join(",");
  const body = rows
    .map((r) => headers.map((h) => esc(r[h.key])).join(","))
    .join("\n");
  return `${head}\n${body}`;
}

function downloadCSV(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    document.title = "Admin · Great Rate Check";
    const meta = document.head.querySelector('meta[name="robots"]');
    const el = meta || document.createElement("meta");
    el.setAttribute("name", "robots");
    el.setAttribute("content", "noindex,nofollow");
    if (!meta) document.head.appendChild(el);

    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setAuthed(false);
      setBooting(false);
      return;
    }
    api
      .get("/auth/me")
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      })
      .finally(() => setBooting(false));
  }, []);

  if (booting) {
    return (
      <div className="min-h-screen bg-navy-deep flex items-center justify-center">
        <Loader2 className="animate-spin text-lime" size={32} />
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-center" richColors />
      {authed ? (
        <Dashboard onLogout={() => setAuthed(false)} />
      ) : (
        <Login onSuccess={() => setAuthed(true)} />
      )}
    </>
  );
}

/* ------------------------------- Login ------------------------------- */
function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grain-bg noise flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center rounded-2xl bg-white/95 px-4 py-2 shadow-soft">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-9 w-auto" />
          </div>
        </div>
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="rounded-3xl bg-white border border-linen-border p-8 shadow-lift"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-navy text-lime mb-5">
            <Lock size={22} />
          </div>
          <h1 className="font-heading font-medium text-2xl text-navy">Admin sign in</h1>
          <p className="mt-1 text-sm text-ink-500">Leads &amp; handoff analytics dashboard.</p>

          <div className="mt-6">
            <GoogleSignInButton returnPath="/admin" testid="admin-login-google" />
            <AuthDivider label="or use your password" />
          </div>

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-ink-600">Email</span>
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full bg-linen/60 border border-linen-border rounded-xl px-4 py-3 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal"
              placeholder="you@example.com"
            />
          </label>
          <label className="block mt-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-ink-600">Password</span>
            <div className="relative mt-1.5">
              <input
                data-testid="admin-password-input"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-linen/60 border border-linen-border rounded-xl px-4 py-3 pr-11 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="admin-password-toggle"
                className="absolute inset-y-0 right-2 flex items-center px-2 text-ink-500 hover:text-navy"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </label>

          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-sm text-destructive font-medium">
              {error}
            </p>
          )}

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-full transition-colors"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Sign in"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

/* ----------------------------- Dashboard ----------------------------- */
function Dashboard({ onLogout }) {
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [reports, setReports] = useState([]);
  const [assets, setAssets] = useState([]);
  const [applications, setApplications] = useState([]);
  const [bdms, setBdms] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [mlos, setMlos] = useState([]);
  const [months, setMonths] = useState([]);
  const [selMonth, setSelMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("today");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [attributions, setAttributions] = useState([]);

  // Reference codes power ⌘K lookups ("who sent GRC-7K2M?") — fetched once.
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/attributions?limit=400`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("grc_admin_token")}` },
      })
      .then((r) => setAttributions(r.data?.rows || []))
      .catch(() => setAttributions([]));
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const [q, setQ] = useState("");
  const [leadSourceFilter, setLeadSourceFilter] = useState("all"); // "all" | "form" | "apply_intent"
  const [leadMloFilter, setLeadMloFilter] = useState("all"); // "all" | "house" | <slug>
  const [activeLead, setActiveLead] = useState(null);
  const [showArchivedLeads, setShowArchivedLeads] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l, c, r] = await Promise.all([
        api.get("/admin/summary"),
        api.get(`/admin/leads${showArchivedLeads ? "?archived=true" : ""}`),
        api.get("/admin/apply-clicks"),
        api.get("/admin/bdm-reports"),
      ]);
      setSummary(s.data);
      setLeads(l.data);
      setClicks(c.data);
      setReports(r.data);
      try {
        const a = await api.get("/library/assets");
        setAssets(a.data || []);
      } catch { /* ignore */ }
      try {
        const ap = await api.get("/admin/bdm-applications");
        setApplications(ap.data || []);
      } catch { /* ignore */ }
      try {
        const mm = await api.get("/admin/mlos");
        setMlos(mm.data || []);
      } catch { /* ignore */ }
      try {
        const bd = await api.get("/admin/bdms");
        setBdms(bd.data || []);
      } catch { /* ignore */ }
      try {
        const iq = await api.get("/admin/brokerage-inquiries");
        setInquiries(iq.data || []);
      } catch { /* ignore */ }
      try {
        const m = await api.get("/admin/bdm-reports/months");
        setMonths(m.data);
        setSelMonth((prev) => prev || (m.data.length ? `${m.data[0].month}|${m.data[0].year}` : ""));
      } catch { /* ignore */ }
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const openFile = async (fileId) => {
    try {
      const res = await api.get(`/admin/bdm-reports/file/${fileId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      /* ignore */
    }
  };

  const downloadMonthPDF = async () => {
    if (!selMonth) return;
    const [month, year] = selMonth.split("|");
    try {
      const res = await api.get(`/admin/bdm-reports/month/pdf?month=${encodeURIComponent(month)}&year=${encodeURIComponent(year)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Partner-Verifications-${month}-${year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  };

  const downloadReportPDF = async (report) => {
    try {
      const res = await api.get(`/admin/bdm-reports/${report.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BDM-Verification-${(report.bdm_name || "report").replace(/\s+/g, "-")}-${(report.period_label || "").replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchivedLeads]);

  const logout = () => {
    clearGoogleSession();
    localStorage.removeItem(TOKEN_KEY);
    onLogout();
  };

  const authHeader = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` }),
    [],
  );

  const filteredLeads = useMemo(() => {
    let list = leads;
    if (leadSourceFilter === "apply_intent") {
      list = list.filter((l) => l.source === "apply_intent");
    } else if (leadSourceFilter === "form") {
      list = list.filter((l) => l.source !== "apply_intent");
    }
    if (leadMloFilter === "house") {
      list = list.filter((l) => !l.mlo_slug);
    } else if (leadMloFilter !== "all") {
      list = list.filter((l) => l.mlo_slug === leadMloFilter);
    }
    if (!q) return list;
    const s = q.toLowerCase();
    return list.filter((l) =>
      [l.full_name, l.email, l.phone, l.state, l.loan_purpose, l.mlo_slug]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [leads, q, leadSourceFilter, leadMloFilter]);

  const leadSourceCounts = useMemo(() => {
    const intent = leads.filter((l) => l.source === "apply_intent").length;
    return { all: leads.length, form: leads.length - intent, apply_intent: intent };
  }, [leads]);

  const clicksByClickId = useMemo(() => {
    const map = {};
    for (const c of clicks) {
      if (c.click_id) map[c.click_id] = c;
    }
    return map;
  }, [clicks]);

  const filteredReports = useMemo(() => {
    const mapped = reports.map((r) => ({
      ...r,
      _scope: (r.scope_of_work || []).join("; "),
      _files: (r.uploads || []).length,
    }));
    if (!q) return mapped;
    const s = q.toLowerCase();
    return mapped.filter((r) =>
      [r.bdm_name, r.email, r.period_label, r.loan_officer_name, r.role, r._scope]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [reports, q]);

  const filteredClicks = useMemo(() => {
    if (!q) return clicks;
    const s = q.toLowerCase();
    return clicks.filter((c) =>
      [c.source, c.utm_source, c.utm_campaign, c.referrer, c.ip]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [clicks, q]);

  const filteredAssets = useMemo(() => {
    const sorted = [...assets].sort((a, b) => (b.download_count || 0) - (a.download_count || 0));
    if (!q) return sorted;
    const s = q.toLowerCase();
    return sorted.filter((a) =>
      [a.filename, a.description, a.category, a.uploaded_by]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [assets, q]);

  const totalDownloads = useMemo(
    () => assets.reduce((sum, a) => sum + (a.download_count || 0), 0),
    [assets],
  );

  const [appTypeFilter, setAppTypeFilter] = useState("all"); // "all" | "bdm" | "mlo"
  const [referralFilter, setReferralFilter] = useState("all"); // "all" | "assigned" | "unassigned"

  const filteredApplications = useMemo(() => {
    let list = [...applications].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
    if (appTypeFilter === "bdm") {
      list = list.filter((a) => (a.application_type || "bdm") === "bdm");
    } else if (appTypeFilter === "mlo") {
      list = list.filter((a) => a.application_type === "mlo");
    }
    if (referralFilter === "assigned") {
      list = list.filter((a) => (a.application_type || "bdm") === "bdm" && !!a.referring_mlo_slug);
    } else if (referralFilter === "unassigned") {
      list = list.filter((a) => (a.application_type || "bdm") === "bdm" && !a.referring_mlo_slug);
    }
    if (!q) return list;
    const s = q.toLowerCase();
    return list.filter((a) =>
      [a.full_name, a.email, a.company, a.city_state, a.classification, a.status, a.referring_mlo_slug]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [applications, q, appTypeFilter, referralFilter]);

  const appTypeCounts = useMemo(() => {
    const mlo = applications.filter((a) => a.application_type === "mlo").length;
    return { all: applications.length, bdm: applications.length - mlo, mlo };
  }, [applications]);

  const referralCounts = useMemo(() => {
    const bdms = applications.filter((a) => (a.application_type || "bdm") === "bdm");
    const assigned = bdms.filter((a) => !!a.referring_mlo_slug).length;
    return { all: bdms.length, assigned, unassigned: bdms.length - assigned };
  }, [applications]);

  const stats = [
    {
      label: "Total leads",
      value: summary?.totals?.leads ?? 0,
      sub: `${summary?.last_7_days?.leads ?? 0} in last 7d`,
      caption: "People who asked us to check their rate",
      icon: <Users size={15} />,
    },
    {
      label: "Apply-Now clicks",
      value: summary?.totals?.apply_clicks ?? 0,
      sub: `${summary?.last_7_days?.apply_clicks ?? 0} in last 7d`,
      caption: "Clicked through to a lender application",
      icon: <MousePointerClick size={15} />,
    },
    {
      label: "Clicks (30d)",
      value: summary?.last_30_days?.apply_clicks ?? 0,
      sub: `${summary?.last_30_days?.leads ?? 0} leads (30d)`,
      caption: "Application clicks in the last 30 days",
      icon: <TrendingUp size={15} />,
    },
  ];

  // Four groups instead of twelve tabs. Each group opens on its first view.
  const NAV = [
    { key: "today", label: "Today", views: [{ key: "today", label: "Today" }] },
    {
      key: "people",
      label: "People",
      views: [
        { key: "leads", label: "Leads", count: leads.length },
        { key: "applications", label: "Applicants", count: applications.length },
        { key: "bdms", label: "Partners", count: bdms.length },
        { key: "bdmteam", label: "Partner team" },
        { key: "mlos", label: "Officers", count: mlos.length },
        { key: "inquiries", label: "Inquiries", count: inquiries.length },
        { key: "reports", label: "Reports", count: reports.length },
      ],
    },
    {
      key: "marketing",
      label: "Marketing",
      views: [
        { key: "comms", label: "Field Updates" },
        { key: "annual", label: "Annual Rate Check" },
        { key: "library", label: "Library", count: assets.length },
        { key: "links", label: "Links" },
        { key: "sends", label: "Send history" },
        { key: "traffic", label: "Traffic", count: clicks.length },
      ],
    },
    {
      key: "settings",
      label: "Settings",
      views: [
        { key: "program", label: "Program & activation" },
        { key: "attribution", label: "Attribution" },
        { key: "brokerages", label: "Brokerages" },
      ],
    },
  ];
  const activeGroup = (NAV.find((g) => g.views.some((v) => v.key === tab)) || NAV[0]).key;

  const paletteViews = NAV.flatMap((g) =>
    g.views.map((v) => ({
      label: g.key === "today" ? "Today" : `${g.label} → ${v.label}`,
      hint: typeof v.count === "number" ? `${v.count} record${v.count !== 1 ? "s" : ""}` : "",
      run: () => setTab(v.key),
    })),
  );
  const paletteActions = [
    { label: "Write this week's Field Update", hint: "Marketing → Field Updates", run: () => setTab("comms") },
    { label: "Resend a past Field Update", hint: "Marketing → Field Updates", run: () => setTab("comms") },
    { label: "Add a loan officer", hint: "People → Officers", run: () => setTab("mlos") },
    { label: "Add a brokerage roster", hint: "Settings → Brokerages", run: () => setTab("brokerages") },
    { label: "Review partner applicants", hint: "People → Applicants", run: () => setTab("applications") },
    { label: "Confirm reference codes", hint: "Settings → Attribution", run: () => setTab("attribution") },
    { label: "Open or close the Annual Rate Check season", hint: "Marketing → Annual Rate Check", run: () => setTab("annual") },
    { label: "Send the Annual Rate Check kickoff", hint: "Marketing → Annual Rate Check", run: () => setTab("annual") },
    { label: "Refresh all data", hint: "Reload every table", run: () => load() },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-linen">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-navy-deep text-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center rounded-xl bg-white/95 px-2.5 py-1.5">
              <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-6 w-auto" />
            </div>
            <span className="text-sm text-white/60 hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              data-testid="admin-library-link"
              href="/library"
              className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white px-3 py-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <FolderOpen size={16} /> Library
            </a>
            <a
              data-testid="admin-reports-link"
              href="/partnerreports"
              className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white px-3 py-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <FileText size={16} /> Reports Form
            </a>
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white px-3 py-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm font-medium bg-lime text-navy-deep px-4 py-2 rounded-full hover:bg-lime-hover transition-colors"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <CommandPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          views={paletteViews}
          actions={paletteActions}
          mlos={mlos.map((m) => ({
            ...m,
            run: () => { setTab("mlos"); setQ(m.name || m.slug); },
          }))}
          bdms={bdms.map((b) => ({
            ...b,
            run: () => { setTab("bdms"); setQ(b.full_name || b.email); },
          }))}
          leads={leads.map((l) => ({
            ...l,
            run: () => { setTab("leads"); setQ(l.email || l.full_name); },
          }))}
          codes={attributions.map((c) => ({
            ...c,
            run: () => setTab("attribution"),
          }))}
        />
        {/* Vital signs — compact by design; the full traffic detail lives in Marketing → Traffic */}
        {tab !== "today" && tab !== "traffic" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="admin-stats-strip">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                data-testid={`admin-stat-${i}`}
                className="rounded-2xl bg-white border border-linen-border px-4 py-3"
              >
                <div className="flex items-center gap-2 text-ink-500">
                  <span className="text-teal">{s.icon}</span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em]">{s.label}</span>
                </div>
                <div className="mt-1.5 flex items-baseline gap-2">
                  <span className="font-heading font-medium text-2xl text-navy tabular-nums leading-none">{s.value}</span>
                  <span className="text-[11px] text-ink-400">{s.sub}</span>
                </div>
                <p className="mt-1 text-[11px] text-ink-500 leading-snug">{s.caption}</p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Navigation — four groups, then the views inside the active group */}
        <div className="mt-8 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="inline-flex bg-navy rounded-full p-1" data-testid="admin-groups">
              {NAV.map((g) => (
                <button
                  key={g.key}
                  onClick={() => setTab(g.views[0].key)}
                  data-testid={`admin-group-${g.key}`}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                    activeGroup === g.key ? "bg-lime text-navy-deep" : "text-white/70 hover:text-white"
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setPaletteOpen(true)}
              data-testid="admin-palette-trigger"
              className="inline-flex items-center gap-2 text-xs text-ink-500 hover:text-navy bg-white border border-linen-border rounded-full px-4 py-2 transition-colors"
            >
              <Search size={13} /> Jump to anything
              <kbd className="font-sans text-[10px] font-bold bg-linen border border-linen-border rounded px-1.5 py-0.5">⌘K</kbd>
            </button>
          </div>

          {activeGroup !== "today" && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="admin-subtabs">
              {NAV.find((g) => g.key === activeGroup).views.map((v) => (
                <button
                  key={v.key}
                  onClick={() => setTab(v.key)}
                  data-testid={`admin-tab-${v.key}`}
                  className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
                    tab === v.key
                      ? "bg-white border border-teal text-navy shadow-soft"
                      : "border border-transparent text-ink-500 hover:text-navy hover:bg-white/60"
                  }`}
                >
                  {v.label}
                  {typeof v.count === "number" ? <span className="text-ink-400"> ({v.count})</span> : null}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div />
          <div className="flex items-center gap-2 flex-wrap">
            {tab === "reports" && months.length > 0 && (
              <div className="flex items-center gap-2">
                <select
                  data-testid="admin-month-select"
                  value={selMonth}
                  onChange={(e) => setSelMonth(e.target.value)}
                  className="py-2 px-3 rounded-full bg-white border border-linen-border text-sm focus:outline-none focus:ring-2 focus:ring-teal/40"
                >
                  {months.map((m) => (
                    <option key={`${m.month}|${m.year}`} value={`${m.month}|${m.year}`}>
                      {m.month} {m.year} ({m.count})
                    </option>
                  ))}
                </select>
                <button
                  data-testid="admin-month-pdf"
                  onClick={downloadMonthPDF}
                  className="inline-flex items-center gap-2 text-sm font-medium bg-teal text-white px-4 py-2 rounded-full hover:bg-teal-hover transition-colors whitespace-nowrap"
                >
                  <Download size={15} /> Month PDF
                </button>
              </div>
            )}
            {tab !== "today" && tab !== "program" && tab !== "attribution" && tab !== "brokerages" && (
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
              <input
                data-testid="admin-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="pl-9 pr-3 py-2 rounded-full bg-white border border-linen-border text-sm focus:outline-none focus:ring-2 focus:ring-teal/40 w-56"
              />
            </div>
            )}
            {tab !== "links" && tab !== "sends" && tab !== "bdmteam" && tab !== "today"
              && tab !== "program" && tab !== "attribution" && tab !== "brokerages" && tab !== "comms" && (
            <button
              data-testid="admin-export"
              onClick={() =>
                tab === "reports"
                  ? downloadCSV(
                      "grc-bdm-reports.csv",
                      toCSV(filteredReports, [
                        { key: "bdm_name", label: "Name" },
                        { key: "role", label: "Role" },
                        { key: "period_label", label: "Period" },
                        { key: "loan_officer_name", label: "Loan Officer" },
                        { key: "email", label: "Email" },
                        { key: "_scope", label: "Scope" },
                        { key: "_files", label: "Files" },
                        { key: "signature", label: "Signature" },
                        { key: "created_at", label: "Submitted" },
                      ])
                    )
                  : tab === "leads"
                  ? downloadCSV(
                      "grc-leads.csv",
                      toCSV(filteredLeads, [
                        { key: "full_name", label: "Name" },
                        { key: "email", label: "Email" },
                        { key: "phone", label: "Phone" },
                        { key: "source", label: "Source" },
                        { key: "mlo_slug", label: "MLO Page" },
                        { key: "loan_amount", label: "Loan Amount" },
                        { key: "loan_purpose", label: "Purpose" },
                        { key: "state", label: "State" },
                        { key: "message", label: "Message" },
                        { key: "created_at", label: "Created" },
                      ])
                    )
                  : tab === "traffic"
                  ? downloadCSV(
                      "grc-apply-clicks.csv",
                      toCSV(filteredClicks, [
                        { key: "source", label: "Source" },
                        { key: "utm_source", label: "UTM Source" },
                        { key: "utm_campaign", label: "UTM Campaign" },
                        { key: "referrer", label: "Referrer" },
                        { key: "landing_path", label: "Landing Path" },
                        { key: "ip", label: "IP" },
                        { key: "country", label: "Country" },
                        { key: "created_at", label: "Created" },
                      ])
                    )
                  : tab === "library"
                  ? downloadCSV(
                      "grc-library-downloads.csv",
                      toCSV(filteredAssets, [
                        { key: "filename", label: "Filename" },
                        { key: "category", label: "Category" },
                        { key: "download_count", label: "Downloads" },
                        { key: "size", label: "Size (bytes)" },
                        { key: "content_type", label: "Type" },
                        { key: "description", label: "Description" },
                        { key: "uploaded_by", label: "Uploaded By" },
                        { key: "created_at", label: "Uploaded" },
                      ])
                    )
                  : tab === "inquiries"
                  ? downloadCSV(
                      "grc-brokerage-inquiries.csv",
                      toCSV(inquiries, [
                        { key: "full_name", label: "Contact" },
                        { key: "brokerage_name", label: "Brokerage" },
                        { key: "agent_count", label: "Agents" },
                        { key: "email", label: "Email" },
                        { key: "phone", label: "Phone" },
                        { key: "city_state", label: "City / State" },
                        { key: "message", label: "Message" },
                        { key: "status", label: "Status" },
                        { key: "created_at", label: "Received" },
                      ])
                    )
                  : tab === "bdms"
                  ? downloadCSV(
                      "grc-bdm-roster.csv",
                      toCSV(bdms, [
                        { key: "full_name", label: "Name" },
                        { key: "email", label: "Email" },
                        { key: "mobile_phone", label: "Phone" },
                        { key: "company", label: "Company" },
                        { key: "city_state", label: "City / State" },
                        { key: "classification", label: "Tier" },
                        { key: "score_total", label: "Score" },
                        { key: "referring_mlo_name", label: "Assigned MLO" },
                        { key: "packets_sent", label: "Packets Sent" },
                        { key: "reports_submitted", label: "Reports" },
                        { key: "approved_at", label: "Approved" },
                        { key: "last_activity", label: "Last Activity" },
                      ])
                    )
                  : downloadCSV(
                      "grc-bdm-applications.csv",
                      toCSV(filteredApplications, [
                        { key: "full_name", label: "Name" },
                        { key: "email", label: "Email" },
                        { key: "mobile_phone", label: "Phone" },
                        { key: "company", label: "Company" },
                        { key: "city_state", label: "City / State" },
                        { key: "referred_by", label: "Referred By" },
                        { key: "score_total", label: "Score" },
                        { key: "classification", label: "Tier" },
                        { key: "status", label: "Status" },
                        { key: "q5_sphere_size", label: "Sphere Size" },
                        { key: "q7_crm_frequency", label: "CRM Frequency" },
                        { key: "q10_conversations_per_month", label: "Conversations/Month" },
                        { key: "q20_coachability", label: "Coachability" },
                        { key: "created_at", label: "Submitted" },
                      ])
                    )
              }
              className="inline-flex items-center gap-2 text-sm font-medium bg-navy text-white px-4 py-2 rounded-full hover:bg-navy-light transition-colors"
            >
              <Download size={15} /> Export CSV
            </button>
            )}
          </div>
        </div>

        {/* Tables */}
        <div className="mt-4">
          {loading ? (
            <div className="py-20 flex justify-center rounded-2xl bg-white border border-linen-border">
              <Loader2 className="animate-spin text-teal" size={26} />
            </div>
          ) : tab === "today" ? (
            <CommandCenter onGoToTab={setTab} />
          ) : tab === "reports" ? (
            <ReportsList rows={filteredReports} openFile={openFile} downloadReportPDF={downloadReportPDF} />
          ) : tab === "leads" ? (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="admin-leads-source-filter">
                <span className="text-xs uppercase tracking-widest text-ink-500 mr-1">Show:</span>
                {[
                  { key: "all", label: "All", count: leadSourceCounts.all },
                  { key: "form", label: "Form leads", count: leadSourceCounts.form },
                  { key: "apply_intent", label: "Apply-intent", count: leadSourceCounts.apply_intent },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setLeadSourceFilter(opt.key)}
                    data-testid={`admin-leads-source-${opt.key}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                      leadSourceFilter === opt.key
                        ? "bg-navy text-white shadow"
                        : "bg-white border border-linen-border text-ink-600 hover:border-teal"
                    }`}
                  >
                    {opt.label}
                    <span className={`ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded-full ${
                      leadSourceFilter === opt.key ? "bg-white/20" : "bg-linen"
                    }`}>{opt.count}</span>
                  </button>
                ))}
                <select
                  value={leadMloFilter}
                  onChange={(e) => setLeadMloFilter(e.target.value)}
                  data-testid="admin-leads-mlo-filter"
                  className="ml-1 rounded-full bg-white border border-linen-border text-xs px-3 py-1.5 text-ink-600 focus:outline-none focus:ring-2 focus:ring-teal/40"
                >
                  <option value="all">All pages</option>
                  <option value="house">House (greatratecheck.com)</option>
                  {mlos.map((m) => (
                    <option key={m.slug} value={m.slug}>/{m.slug} — {m.name}</option>
                  ))}
                </select>
              </div>
              <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
                <LeadsTable
                  rows={filteredLeads}
                  clicksByClickId={clicksByClickId}
                  onOpen={setActiveLead}
                  onChanged={load}
                  showingArchived={showArchivedLeads}
                  onToggleArchived={() => setShowArchivedLeads((v) => !v)}
                />
              </div>
            </>
          ) : tab === "traffic" ? (
            <div className="space-y-5" data-testid="admin-traffic">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <BreakdownCard
                  testid="admin-by-source"
                  title="Clicks by button / source"
                  rows={summary?.clicks_by_source || []}
                  labelKey="source"
                />
                <BreakdownCard
                  testid="admin-by-utm"
                  title="Clicks by campaign (utm_source)"
                  rows={summary?.clicks_by_utm_source || []}
                  labelKey="utm_source"
                />
              </div>
              <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
                <ClicksTable rows={filteredClicks} />
              </div>
            </div>
          ) : tab === "library" ? (
            <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
              <LibraryDownloadsTable rows={filteredAssets} totalDownloads={totalDownloads} />
            </div>
          ) : tab === "mlos" ? (
            <MLOsPanel />
          ) : tab === "links" ? (
            <LinksPanel mlos={mlos} q={q} />
          ) : tab === "sends" ? (
            <SendHistoryPanel q={q} />
          ) : tab === "comms" ? (
            <div className="space-y-6">
              <ShelfAnnounceCard />
              <CommsPanel />
            </div>
          ) : tab === "annual" ? (
            <div className="space-y-6" data-testid="admin-annual-view">
              <AnnualCheckPanel />
            </div>
          ) : tab === "bdms" ? (
            <BDMsPanel bdms={bdms} q={q} />
          ) : tab === "bdmteam" ? (
            <div className="space-y-6">
              <BDMTeamPanel mlos={mlos} q={q} />
              <ApplicantPipelineCard endpoint="/admin/applicant-pipeline" authHeader={authHeader} />
              <BDMFilingsPanel />
              <AssetLeaderboardPanel />
              <BDMShelfPanel mlos={mlos} />
            </div>
          ) : tab === "program" ? (
            <div className="space-y-6" data-testid="admin-program-view">
              <PartnerActivationCard />
              <JourneyPanel authHeader={authHeader} />
              <WelcomeVideoCard scope="house" authHeader={authHeader} />
            </div>
          ) : tab === "attribution" ? (
            <div className="space-y-6" data-testid="admin-attribution-view">
              <AttributionsCard />
              <AttributionProbeCard authHeader={authHeader} />
            </div>
          ) : tab === "brokerages" ? (
            <div className="space-y-6" data-testid="admin-brokerages-view">
              <BrokeragesCard />
            </div>
          ) : tab === "inquiries" ? (
            <InquiriesPanel
              inquiries={inquiries}
              q={q}
              onChanged={(id, update) =>
                setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, ...update } : i)))
              }
            />
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="admin-apps-type-filter">
                <span className="text-xs uppercase tracking-widest text-ink-500 mr-1">Type:</span>
                {[
                  { key: "all", label: "All", count: appTypeCounts.all },
                  { key: "bdm", label: "Partner (BDM)", count: appTypeCounts.bdm },
                  { key: "mlo", label: "MLO Growth", count: appTypeCounts.mlo },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setAppTypeFilter(opt.key)}
                    data-testid={`admin-apps-type-${opt.key}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                      appTypeFilter === opt.key
                        ? "bg-navy text-white shadow"
                        : "bg-white border border-linen-border text-ink-600 hover:border-teal"
                    }`}
                  >
                    {opt.label}
                    <span className={`ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded-full ${
                      appTypeFilter === opt.key ? "bg-white/20" : "bg-linen"
                    }`}>{opt.count}</span>
                  </button>
                ))}
              </div>
              <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="admin-apps-referral-filter">
                <span className="text-xs uppercase tracking-widest text-ink-500 mr-1">Referral:</span>
                {[
                  { key: "all", label: "All BDMs", count: referralCounts.all },
                  { key: "assigned", label: "Assigned", count: referralCounts.assigned },
                  { key: "unassigned", label: "Unassigned", count: referralCounts.unassigned },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setReferralFilter(opt.key)}
                    data-testid={`admin-apps-referral-${opt.key}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                      referralFilter === opt.key
                        ? (opt.key === "unassigned" && opt.count > 0
                            ? "bg-amber-500 text-white shadow"
                            : "bg-navy text-white shadow")
                        : "bg-white border border-linen-border text-ink-600 hover:border-teal"
                    }`}
                  >
                    {opt.label}
                    <span className={`ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded-full ${
                      referralFilter === opt.key ? "bg-white/20" : "bg-linen"
                    }`}>{opt.count}</span>
                  </button>
                ))}
              </div>
              <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
                <ApplicationsTable rows={filteredApplications} onRefresh={load} />
              </div>
            </>
          )}
        </div>
      </main>

      {activeLead && (
        <LeadDetail
          lead={activeLead}
          mlos={mlos}
          clicksByClickId={clicksByClickId}
          onClose={() => setActiveLead(null)}
          onSaved={(updated) => {
            if (updated) {
              setLeads((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
            }
          }}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid, icon: Icon }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
        active ? "bg-navy text-white" : "text-ink-600 hover:text-navy"
      }`}
    >
      {Icon ? (
        <span
          className={`inline-flex items-center justify-center w-6 h-6 rounded-full transition-colors ${
            active ? "bg-white/20 text-white" : "bg-linen-border/70 text-ink-500"
          }`}
        >
          <Icon size={13} />
        </span>
      ) : null}
      {children}
    </button>
  );
}

function BreakdownCard({ title, rows, labelKey, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div data-testid={testid} className="rounded-2xl bg-white border border-linen-border p-6 shadow-soft">
      <h3 className="font-heading font-medium text-lg text-navy">{title}</h3>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-ink-400">No data yet.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {rows.map((r, i) => (
            <li key={i}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-navy font-medium truncate pr-2">{r[labelKey]}</span>
                <span className="text-ink-500 tabular-nums">{r.count}</span>
              </div>
              <div className="h-2 rounded-full bg-linen overflow-hidden">
                <div
                  className="h-full rounded-full bg-teal"
                  style={{ width: `${(r.count / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MLOsPanel() {
  const [mlos, setMlos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null); // null | "new" | slug
  const [regen, setRegen] = useState(false);
  const [sampleSlug, setSampleSlug] = useState("");
  const [savingSample, setSavingSample] = useState(false);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("name");

  const loadSample = async () => {
    try {
      const { data } = await api.get("/site/walkthrough-sample");
      setSampleSlug(data?.slug || "");
    } catch (e) {
      setSampleSlug("");
    }
  };

  const saveSample = async (slug) => {
    setSampleSlug(slug);
    setSavingSample(true);
    try {
      await api.put("/admin/site/walkthrough-sample", { slug });
      toast.success(slug ? `Walkthrough now shows /${slug}.` : "Walkthrough sample cleared.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that.");
    } finally {
      setSavingSample(false);
    }
  };

  const regeneratePreviews = async () => {
    setRegen(true);
    try {
      const { data } = await api.post("/admin/og/regenerate");
      toast.success(`Refreshed ${data.rendered.length} link preview card${data.rendered.length !== 1 ? "s" : ""}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't refresh previews.");
    } finally {
      setRegen(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("grc_admin_token");
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/mlos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMlos(res.data || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadSample(); }, []);

  const activeMlo = showForm && showForm !== "new" ? mlos.find((m) => m.slug === showForm) : null;

  const needle = filter.trim().toLowerCase();
  const visible = mlos
    .filter((m) =>
      !needle ||
      `${m.name} ${m.slug} ${m.nmls} ${(m.licensed_states || []).join(" ")} ${m.email || ""}`
        .toLowerCase()
        .includes(needle),
    )
    .sort((a, b) => {
      if (sort === "setup") {
        return Object.keys(mloSetupGaps(b)).length - Object.keys(mloSetupGaps(a)).length;
      }
      if (sort === "states") {
        return (b.licensed_states || []).length - (a.licensed_states || []).length;
      }
      if (sort === "status") {
        return Number(b.active) - Number(a.active);
      }
      return (a.name || a.slug).localeCompare(b.name || b.slug);
    });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="font-heading text-xl text-navy">MLO branded pages</h3>
          <p className="text-sm text-ink-500 mt-1">Each MLO gets an instant page at greatratecheck.com/[slug].</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={regeneratePreviews}
            disabled={regen}
            data-testid="admin-og-regenerate"
            title="Rebuild every officer's link-preview card from their current photo, name and NMLS"
            className="inline-flex items-center gap-2 rounded-xl bg-white border border-linen-border hover:border-teal text-navy px-4 py-2.5 text-sm"
          >
            {regen ? <Loader2 size={15} className="animate-spin" /> : null}
            Refresh link previews
          </button>
          <button
            onClick={() => setShowForm("new")}
            data-testid="admin-mlo-new"
            className="inline-flex items-center gap-2 rounded-xl bg-teal hover:bg-teal/90 text-white px-4 py-2.5 text-sm shadow"
          >
            + New MLO
          </button>
        </div>
      </div>

      {/* Which officer page the /teamleaders live walkthrough embeds */}
      <div className="mb-4 rounded-2xl bg-white border border-linen-border shadow-soft p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-widest text-teal">Team Leaders walkthrough sample</p>
          <p className="text-sm text-ink-500 mt-1">
            The live page embedded on /teamleaders. Picking someone here does not hide them from the network.
          </p>
        </div>
        <select
          value={sampleSlug}
          onChange={(e) => saveSample(e.target.value)}
          disabled={savingSample}
          data-testid="admin-walkthrough-sample"
          className="rounded-xl border border-linen-border bg-white p-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-teal/40 shrink-0"
        >
          <option value="">None — show the fallback card</option>
          {mlos.map((m) => (
            <option key={m.slug} value={m.slug}>
              /{m.slug} — {m.name}{m.demo ? " (hidden from network)" : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-ink-500">Loading…</div>
        ) : mlos.length === 0 ? (
          <div className="p-6 text-sm text-ink-500">No MLOs yet. Click "+ New MLO" to add one.</div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-linen-border flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter officers…"
                  data-testid="admin-mlo-filter"
                  className="pl-8 pr-3 py-1.5 rounded-full bg-linen/60 border border-linen-border text-xs w-52 focus:outline-none focus:ring-2 focus:ring-teal/30"
                />
              </div>
              <div className="flex items-center gap-1.5 text-[11px]">
                <span className="text-ink-400 uppercase tracking-wider font-bold">Sort</span>
                {[
                  { key: "name", label: "Name" },
                  { key: "setup", label: "Setup" },
                  { key: "states", label: "States" },
                  { key: "status", label: "Status" },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSort(s.key)}
                    data-testid={`admin-mlo-sort-${s.key}`}
                    className={`px-2.5 py-1 rounded-full font-semibold transition-colors ${
                      sort === s.key ? "bg-navy text-white" : "text-ink-500 hover:text-navy hover:bg-linen"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <span className="ml-auto text-[11px] text-ink-500">
                {visible.length} of {mlos.length} officers
              </span>
            </div>
            <table className="w-full text-sm" data-testid="admin-mlos-table">
              <thead>
                <tr className="text-left text-ink-500 border-b border-linen-border bg-linen/50">
                  <Th>Officer</Th><Th>States</Th><Th>Setup</Th><Th>Invite</Th><Th>Status</Th><Th></Th>
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => (
                <tr key={m.slug} className="border-b border-linen-border/60 hover:bg-linen/40" data-testid={`admin-mlo-row-${m.slug}`}>
                  <Td>
                    <div className="flex items-center gap-3 min-w-0">
                      {m.has_photo ? (
                        <img
                          src={mloPhotoUrl(m)}
                          alt={m.name}
                          data-testid={`admin-mlo-thumb-${m.slug}`}
                          className="w-9 h-9 object-cover object-top rounded-full border border-linen-border shrink-0"
                        />
                      ) : (
                        <span className="inline-flex w-9 h-9 items-center justify-center rounded-full bg-linen text-[9px] text-ink-400 border border-linen-border shrink-0">
                          No&nbsp;pic
                        </span>
                      )}
                      <span className="min-w-0">
                        <span className="block font-medium text-navy truncate">{m.name}</span>
                        <span className="block text-[11px] text-ink-500 truncate">
                          <code className="text-teal font-mono">/{m.slug}</code>
                          {m.nmls ? ` · NMLS ${m.nmls}` : ""}
                        </span>
                      </span>
                    </div>
                  </Td>
                  <Td className="text-ink-500 text-xs">{(m.licensed_states || []).join(", ") || "—"}</Td>
                  <Td>
                    {(() => {
                      const gaps = Object.values(mloSetupGaps(m));
                      return gaps.length === 0 ? (
                        <span className="inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-lime/25 text-navy" data-testid={`admin-mlo-setup-${m.slug}`}>Complete</span>
                      ) : (
                        <span
                          title={`Missing: ${gaps.join(", ")}`}
                          data-testid={`admin-mlo-setup-${m.slug}`}
                          className="inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-300"
                        >
                          {gaps.length} to fix
                        </span>
                      );
                    })()}
                  </Td>
                  <Td>
                    {m.invite_status === "sent" ? (
                      <span
                        title={`Invite emailed ${m.invite_last_sent_at ? new Date(m.invite_last_sent_at).toLocaleString() : ""}`}
                        data-testid={`admin-mlo-invite-${m.slug}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-lime/25 text-navy"
                      >
                        Sent
                      </span>
                    ) : m.invite_status === "failed" ? (
                      <span
                        title={`Email failed: ${m.invite_error || "unknown error"}`}
                        data-testid={`admin-mlo-invite-${m.slug}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-red-100 text-red-700 border border-red-300"
                      >
                        <AlertTriangle className="w-3 h-3" /> Failed
                      </span>
                    ) : (
                      <span data-testid={`admin-mlo-invite-${m.slug}`} className="text-[10px] uppercase tracking-widest text-ink-400">—</span>
                    )}
                  </Td>
                  <Td>
                    <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md ${
                      m.active ? "bg-lime/25 text-navy" : "bg-linen text-ink-500"
                    }`}>
                      {m.active ? "Live" : "Off"}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2 justify-end">
                      <a href={`/${m.slug}`} target="_blank" rel="noopener noreferrer" className="text-teal underline decoration-linen-border underline-offset-4 hover:decoration-teal text-xs">
                        Open
                      </a>
                      <button
                        onClick={() => setShowForm(m.slug)}
                        data-testid={`admin-mlo-edit-${m.slug}`}
                        className="text-xs px-3 py-1 rounded-lg bg-linen hover:bg-linen-border text-navy"
                      >
                        Edit
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {showForm && (
        <MLOForm
          mlo={activeMlo}
          isNew={showForm === "new"}
          onClose={() => setShowForm(null)}
          onSaved={() => { setShowForm(null); load(); }}
        />
      )}
    </div>
  );
}

// One definition of "what's still missing" so the roster pill and the edit
// form always agree on which fields need attention.
const COMPANY_NMLS = "1660690";
function mloSetupGaps(m) {
  const gaps = {};
  if (!m.has_photo) gaps.photo = "Primary photo";
  if (!(m.apply_url || "").trim()) gaps.apply_url = "Their loan application link";
  if (!(m.licensed_states || []).length) gaps.licensed_states = "Licensed states";
  const nmls = String(m.nmls || "").trim();
  if (!nmls || /^tbd$/i.test(nmls) || nmls === COMPANY_NMLS) gaps.nmls = "Personal NMLS (not the company's)";
  if (!(m.name || "").trim().includes(" ")) gaps.name = "Full name (first and last)";
  if (!(m.phone || "").trim()) gaps.phone = "Phone";
  return gaps;
}

function MLOForm({ mlo, isNew, onClose, onSaved }) {
  const [slug, setSlug] = useState(mlo?.slug || "");
  const [name, setName] = useState(mlo?.name || "");
  const [title, setTitle] = useState(mlo?.title || "Mortgage Loan Officer");
  const [nmls, setNmls] = useState(mlo?.nmls || "");
  const [phone, setPhone] = useState(mlo?.phone || "");
  const [email, setEmail] = useState(mlo?.email || "");
  const [city, setCity] = useState(mlo?.city || "");
  const [bio, setBio] = useState(mlo?.bio || "");
  const [tagline, setTagline] = useState(mlo?.tagline || "");
  const [ogHeadline, setOgHeadline] = useState(mlo?.og_headline || "");
  const [applyUrl, setApplyUrl] = useState(mlo?.apply_url || "");
  const [licensedStates, setLicensedStates] = useState((mlo?.licensed_states || []).join(", "));
  const [acceptingLeads, setAcceptingLeads] = useState(mlo?.accepting_leads ?? true);
  const [demo, setDemo] = useState(mlo?.demo ?? false);
  const [yearsExperience, setYearsExperience] = useState(mlo?.years_experience ?? "");
  const [active, setActive] = useState(mlo?.active ?? true);
  const [handlesPacketQuestions, setHandlesPacketQuestions] = useState(mlo?.handles_packet_questions ?? false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoSecondaryFile, setPhotoSecondaryFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("grc_admin_token")}` });

  const save = async (e) => {
    e?.preventDefault();
    if (!name.trim() || !slug.trim() || !nmls.trim()) {
      setErr("Slug, name, and NMLS are required.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      if (isNew) {
        const fd = new FormData();
        fd.append("slug", slug.trim().toLowerCase());
        fd.append("name", name.trim());
        fd.append("nmls", nmls.trim());
        fd.append("title", title.trim());
        fd.append("phone", phone.trim());
        fd.append("email", email.trim());
        fd.append("city", city.trim());
        fd.append("bio", bio.trim());
        fd.append("tagline", tagline.trim());
        fd.append("og_headline", ogHeadline.trim());
        fd.append("apply_url", applyUrl.trim());
        fd.append("licensed_states", licensedStates.trim());
        fd.append("demo", demo ? "true" : "false");
        if (yearsExperience !== "" && Number(yearsExperience) > 0) {
          fd.append("years_experience", String(Number(yearsExperience)));
        }
        fd.append("active", active ? "true" : "false");
        fd.append("handles_packet_questions", handlesPacketQuestions ? "true" : "false");
        if (photoFile) fd.append("photo", photoFile);
        if (photoSecondaryFile) fd.append("photo_secondary", photoSecondaryFile);
        await axios.post(`${API_ROOT}/admin/mlos`, fd, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } });
      } else {
        await axios.patch(`${API_ROOT}/admin/mlos/${mlo.slug}`, {
          name: name.trim(), title: title.trim(), nmls: nmls.trim(),
          phone: phone.trim(), email: email.trim(), city: city.trim(), bio: bio.trim(),
          tagline: tagline.trim(), og_headline: ogHeadline.trim(),
          apply_url: applyUrl.trim(),
          licensed_states: licensedStates.split(",").map((x) => x.trim()).filter(Boolean),
          accepting_leads: acceptingLeads,
          demo,
          years_experience: yearsExperience === "" ? null : Number(yearsExperience),
          active,
          handles_packet_questions: handlesPacketQuestions,
        }, { headers: authHeader() });
        if (photoFile) {
          const fd = new FormData();
          fd.append("photo", photoFile);
          fd.append("slot", "primary");
          await axios.post(`${API_ROOT}/admin/mlos/${mlo.slug}/photo`, fd, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } });
        }
        if (photoSecondaryFile) {
          const fd = new FormData();
          fd.append("photo", photoSecondaryFile);
          fd.append("slot", "secondary");
          await axios.post(`${API_ROOT}/admin/mlos/${mlo.slug}/photo`, fd, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } });
        }
      }
      onSaved();
    } catch (e2) {
      if (e2?.response?.status === 401) {
        // The global interceptor handles the redirect + toast. Show a helpful
        // in-form line so the user isn't confused about what to do.
        setErr("Your session expired. Redirecting you to sign in again — your changes weren't saved.");
      } else {
        setErr(e2?.response?.data?.detail || "Save failed.");
      }
    } finally {
      setBusy(false);
    }
  };

  // Recomputed as you type, so highlights clear the moment a gap is filled.
  const gaps = mloSetupGaps({
    name,
    nmls,
    phone,
    apply_url: applyUrl,
    licensed_states: licensedStates.split(",").map((x) => x.trim()).filter(Boolean),
    has_photo: isNew ? !!photoFile : mlo?.has_photo || !!photoFile,
  });
  const gapKeys = Object.keys(gaps);
  const flag = (key) => (gaps[key] ? " ring-2 ring-amber-400 border-amber-300 bg-amber-50/40" : "");
  const flagLabel = (key) =>
    gaps[key] ? (
      <span className="ml-1.5 text-[10px] font-semibold text-amber-700 normal-case tracking-normal">needs attention</span>
    ) : null;

  const del = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API_ROOT}/admin/mlos/${mlo.slug}`, { headers: authHeader() });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] bg-black/40 flex justify-end" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg h-full bg-linen border-l border-linen-border shadow-2xl overflow-y-auto" data-testid="admin-mlo-form">
        <div className="sticky top-0 z-10 bg-white border-b border-linen-border px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-teal">MLO branded page</p>
            <h3 className="mt-1 font-heading font-medium text-xl text-navy">{isNew ? "New MLO" : `Edit /${mlo.slug}`}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-linen text-ink-500" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={save} className="p-6 space-y-4">
          {gapKeys.length > 0 && (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="admin-mlo-gaps-banner">
              <p className="text-xs font-semibold uppercase tracking-widest text-amber-800">
                {gapKeys.length} thing{gapKeys.length === 1 ? "" : "s"} to finish
              </p>
              <ul className="mt-2 space-y-1">
                {gapKeys.map((k) => (
                  <li key={k} className="text-sm text-amber-900 flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                    {gaps[k]}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-[11px] text-amber-800">
                The fields below are outlined in amber. This clears as soon as you fill them in.
              </p>
            </div>
          )}
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">Slug <span className="text-red-500">*</span></span>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              readOnly={!isNew}
              placeholder="e.g. joe"
              data-testid="admin-mlo-slug"
              className={`mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-white ${!isNew ? "opacity-60" : ""}`}
            />
            <span className="text-[11px] text-ink-500">Page will be at greatratecheck.com/{slug || "..."}</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-500">Name <span className="text-red-500">*</span>{flagLabel("name")}</span>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-mlo-name" className={`mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-white${flag("name")}`} />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-500">NMLS # <span className="text-red-500">*</span>{flagLabel("nmls")}</span>
              <input value={nmls} onChange={(e) => setNmls(e.target.value)} data-testid="admin-mlo-nmls" className={`mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-white${flag("nmls")}`} />
              <span className="text-[11px] text-ink-500">Their personal NMLS — not NEXA's company number ({COMPANY_NMLS}).</span>
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-white" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-500">Phone{flagLabel("phone")}</span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1-425-555-1234" data-testid="admin-mlo-phone" className={`mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-white${flag("phone")}`} />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-500">Email</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-white" />
              <span className="text-[11px] text-ink-500">Leave blank to hide email on the page.</span>
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">City, State (shown in the page footer)</span>
            <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Tampa, FL" data-testid="admin-mlo-city" className="mt-1 w-full sm:max-w-[280px] rounded-xl border border-linen-border p-3 text-sm text-navy bg-white" />
            <span className="text-[11px] text-ink-500">Leave blank to show no location. Tip: you can also use the corporate location — Chandler, AZ.</span>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">Bio (shown in the "Meet …" section)</span>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={5} className="mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-white" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">OG share headline (link preview)</span>
            <textarea
              value={ogHeadline}
              onChange={(e) => setOgHeadline(e.target.value)}
              rows={2}
              placeholder="Check your rate | before you choose | your loan."
              data-testid="admin-mlo-og-headline"
              className="mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-white"
            />
            <span className="text-[11px] text-ink-500">Use <code>|</code> to break lines. Shown on the shared /{slug || "..."} preview image. Leave blank for the default "Check your rate before you choose your loan." — regenerates on save.</span>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">Their loan application link{flagLabel("apply_url")}</span>
            <input
              value={applyUrl}
              onChange={(e) => setApplyUrl(e.target.value)}
              placeholder="https://prod.lendingpad.com/…/guest-application"
              data-testid="admin-mlo-apply-url"
              className={`mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-white${flag("apply_url")}`}
            />
            <span className="text-[11px] text-ink-500">
              Powers the Apply buttons on /{slug || "..."} and /{slug || "..."}/apply. Leave blank and those buttons
              become "Call {(name || "them").split(" ")[0]}" instead — borrowers never get routed to another MLO's application.
            </span>
          </label>
          <div className="rounded-2xl border border-linen-border bg-white p-4 space-y-3">
            <div className="text-xs uppercase tracking-widest text-teal">Lead routing</div>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-500">Licensed states{flagLabel("licensed_states")}</span>
              <input
                value={licensedStates}
                onChange={(e) => setLicensedStates(e.target.value.toUpperCase())}
                placeholder="WA, AZ, CA"
                data-testid="admin-mlo-licensed-states"
                className={`mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-white uppercase${flag("licensed_states")}`}
              />
              <span className="text-[11px] text-ink-500">
                Two-letter codes, comma separated. Main-page inquiries are only routed to officers licensed in the
                property's state. Leave blank and they'll never be auto-matched.
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={acceptingLeads} onChange={(e) => setAcceptingLeads(e.target.checked)} data-testid="admin-mlo-accepting-leads" className="accent-teal" />
              <span className="text-navy">Accepting new leads from the main page</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={demo} onChange={(e) => setDemo(e.target.checked)} data-testid="admin-mlo-demo" className="accent-teal mt-1" />
              <span className="text-navy">
                Hide from the network
                <span className="block text-[11px] text-ink-500">
                  Their page stays live at /{slug || "slug"}, but they're removed from the officer directory,
                  the main-page network grid, and lead routing. For sample or paused profiles only — this does
                  NOT choose the walkthrough sample (that's set on the MLOs tab).
                </span>
              </span>
            </label>
            {!isNew && (
              <p className="text-[11px] text-ink-500" data-testid="admin-mlo-queue-stats">
                Routing queue: {mlo?.lead_count || 0} lead{(mlo?.lead_count || 0) === 1 ? "" : "s"} received
                {mlo?.last_lead_at ? ` · last on ${new Date(mlo.last_lead_at).toLocaleDateString()}` : " · never matched yet"}.
                In shared states, whoever has waited longest goes first.
              </p>
            )}
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">Years of experience (drives the "N+ yrs" pill — leave blank to hide)</span>
            <input
              type="number"
              min={0}
              max={80}
              value={yearsExperience}
              onChange={(e) => setYearsExperience(e.target.value)}
              placeholder="e.g. 30"
              data-testid="admin-mlo-years-experience"
              className="mt-1 w-full sm:max-w-[160px] rounded-xl border border-linen-border p-3 text-sm text-navy bg-white"
            />
            <span className="text-[11px] text-ink-500 block mt-1">Also drives the "Years originating" stat in the Meet section.</span>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">Primary photo (Hero + link preview, JPEG/PNG, up to 8MB){flagLabel("photo")}</span>
            {!isNew && mlo?.has_photo && (
              <img
                src={mloPhotoUrl(mlo)}
                alt={`${mlo.name} primary`}
                data-testid="admin-mlo-photo-current"
                className="mt-2 w-24 h-30 object-cover object-top rounded-xl border border-linen-border"
              />
            )}
            <input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} data-testid="admin-mlo-photo" className="mt-2 w-full text-sm" />
            {!isNew && mlo?.has_photo && !photoFile && (
              <p className="text-[11px] text-ink-500 mt-1">Saved ✓ — choose a new file only if you want to replace it.</p>
            )}
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">Meet-section photo (optional — falls back to primary)</span>
            {!isNew && mlo?.has_photo_secondary && (
              <img
                src={mloPhotoUrl(mlo, "secondary")}
                alt={`${mlo.name} meet-section`}
                data-testid="admin-mlo-photo-secondary-current"
                className="mt-2 w-24 h-30 object-cover object-top rounded-xl border border-linen-border"
              />
            )}
            <input type="file" accept="image/*" onChange={(e) => setPhotoSecondaryFile(e.target.files?.[0] || null)} data-testid="admin-mlo-photo-secondary" className="mt-2 w-full text-sm" />
            {!isNew && mlo?.has_photo_secondary && !photoSecondaryFile && (
              <p className="text-[11px] text-ink-500 mt-1">Saved ✓ — choose a new file only if you want to replace it.</p>
            )}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-teal" />
            <span className="text-navy">Active — page is publicly reachable</span>
          </label>
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={handlesPacketQuestions} onChange={(e) => setHandlesPacketQuestions(e.target.checked)} data-testid="admin-mlo-handles-packet" className="accent-teal" />
              <span className="text-navy">Hands-on — this MLO answers their own packet questions</span>
            </label>
            <p className="text-[11px] text-ink-500 mt-1 ml-6">When checked, shared asset packets show this MLO's name and email as the "Questions?" contact. Otherwise the Great Rate Check program contact is shown.</p>
          </div>

          {!isNew && <MLOAccountPanel slug={mlo.slug} defaultEmail={email} />}

          {err && <p className="text-sm text-red-600" data-testid="admin-mlo-error">{err}</p>}

          <div className="flex items-center justify-between pt-4 border-t border-linen-border">
            {!isNew ? (
              !confirmDelete ? (
                <button type="button" onClick={() => setConfirmDelete(true)} className="text-xs text-red-600 hover:underline">Delete MLO</button>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-red-600">Delete?</span>
                  <button type="button" onClick={del} disabled={busy} className="text-xs px-2 py-1 rounded bg-red-600 text-white">Confirm</button>
                  <button type="button" onClick={() => setConfirmDelete(false)} className="text-xs px-2 py-1 rounded bg-linen text-navy">Cancel</button>
                </div>
              )
            ) : <span />}
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-navy hover:bg-linen">Cancel</button>
              <button type="submit" disabled={busy} data-testid="admin-mlo-save" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-navy hover:bg-[#173a5f] text-white disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {isNew ? "Create MLO" : "Save changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}


function MLOAccountPanel({ slug, defaultEmail }) {
  const [state, setState] = useState({ loading: true, has_account: false, email: null });
  const [showCreate, setShowCreate] = useState(false);
  const [email, setEmail] = useState(defaultEmail || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [revealed, setRevealed] = useState(null); // {email, password, sent, error, kind}
  const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("grc_admin_token")}` });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_ROOT}/admin/mlos/${slug}/account`, { headers: authHeader() });
      setState({ loading: false, ...data });
    } catch {
      setState({ loading: false, has_account: false, email: null });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const create = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(
        `${API_ROOT}/admin/mlos/${slug}/account`,
        { email: email.trim().toLowerCase(), password: password || undefined },
        { headers: authHeader() },
      );
      setRevealed({ email: data.email, password: data.password, sent: data.email_sent, error: data.email_error, kind: "welcome" });
      setShowCreate(false);
      setPassword("");
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Create failed.");
    } finally { setBusy(false); }
  };

  const resetPw = async () => {
    if (!window.confirm(`Reset login password for ${state.email}?`)) return;
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API_ROOT}/admin/mlos/${slug}/account/reset`, {}, { headers: authHeader() });
      setRevealed({ email: data.email, password: data.password, sent: data.email_sent, error: data.email_error, kind: "reset" });
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Reset failed.");
    } finally { setBusy(false); }
  };

  const resendInvite = async () => {
    if (!window.confirm(`Re-send the welcome invite to ${state.email}?\n\nThis issues a NEW password (the original is never stored), so their old password stops working.`)) return;
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API_ROOT}/admin/mlos/${slug}/account/resend`, {}, { headers: authHeader() });
      setRevealed({ email: data.email, password: data.password, sent: data.email_sent, error: data.email_error, kind: "welcome" });
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Re-send failed.");
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete login account for ${state.email}? They will no longer be able to sign in.`)) return;
    setBusy(true); setErr("");
    try {
      await axios.delete(`${API_ROOT}/admin/mlos/${slug}/account`, { headers: authHeader() });
      setRevealed(null);
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Delete failed.");
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return <div className="rounded-2xl bg-linen/70 border border-linen-border p-4 text-sm text-ink-500">Loading login account…</div>;
  }

  const sentAt = state.invite_last_sent_at ? new Date(state.invite_last_sent_at).toLocaleString() : null;

  return (
    <div className="rounded-2xl bg-linen/70 border border-linen-border p-4" data-testid="admin-mlo-account-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-teal">Login account</p>
          <p className="text-sm text-navy mt-0.5">
            {state.has_account ? state.email : "No login account yet — MLO cannot sign in."}
          </p>
          {state.invite_status === "sent" && (
            <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-navy" data-testid="admin-mlo-invite-status">
              <MailCheck className="w-3.5 h-3.5 text-teal" />
              {state.invite_type === "reset" ? "Password reset emailed" : "Invite emailed"}{sentAt ? ` · ${sentAt}` : ""}
            </p>
          )}
          {state.invite_status === "failed" && (
            <p className="mt-1 inline-flex items-start gap-1.5 text-[11px] text-red-700" data-testid="admin-mlo-invite-status">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Last email did <strong>not</strong> send{sentAt ? ` (${sentAt})` : ""} — {state.invite_error || "unknown error"}.
                Fix the address, then re-send.
              </span>
            </p>
          )}
        </div>
        {!state.has_account ? (
          !showCreate && (
            <button type="button" onClick={() => setShowCreate(true)} data-testid="admin-mlo-account-create-btn" className="text-xs px-3 py-1.5 rounded-full bg-navy text-white hover:bg-[#173a5f] shrink-0">
              Create login
            </button>
          )
        ) : (
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={resendInvite} disabled={busy} data-testid="admin-mlo-account-resend-btn" className="text-xs px-3 py-1.5 rounded-full bg-lime text-navy font-semibold hover:brightness-95 disabled:opacity-60">
              Re-send invite
            </button>
            <button type="button" onClick={resetPw} disabled={busy} data-testid="admin-mlo-account-reset-btn" className="text-xs px-3 py-1.5 rounded-full bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60">
              Reset password
            </button>
            <button type="button" onClick={del} disabled={busy} className="text-xs px-3 py-1.5 rounded-full text-red-600 hover:bg-red-50">
              Delete
            </button>
          </div>
        )}
      </div>

      {showCreate && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-500">Email</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-mlo-account-email" className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-white" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-500">Password (leave blank to auto-generate)</span>
              <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-mlo-account-password" className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-white" />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowCreate(false)} className="text-xs px-3 py-1.5 rounded-full text-navy hover:bg-linen">Cancel</button>
            <button type="button" onClick={create} disabled={busy || !email.trim()} data-testid="admin-mlo-account-submit" className="text-xs px-3 py-1.5 rounded-full bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60">
              {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : null} Create account
            </button>
          </div>
        </div>
      )}

      {revealed && (
        <div className="mt-4 rounded-xl bg-lime/20 border border-lime p-3 text-sm" data-testid="admin-mlo-account-revealed">
          {revealed.sent ? (
            <p className="flex items-center gap-1.5 text-navy font-semibold" data-testid="admin-mlo-email-sent">
              <MailCheck className="w-4 h-4 text-teal" />
              Emailed to {revealed.email} — credentials below (shown once):
            </p>
          ) : (
            <div className="mb-3 rounded-lg bg-red-50 border border-red-300 p-3" data-testid="admin-mlo-email-failed">
              <p className="flex items-start gap-1.5 text-red-700 font-semibold text-[13px]">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                The {revealed.kind === "reset" ? "password reset" : "welcome"} email did NOT send.
              </p>
              <p className="mt-1.5 text-[12px] text-red-700 font-mono break-words">{revealed.error || "Unknown error"}</p>
              <p className="mt-2 text-[12px] text-ink-500">
                The account still works — copy the credentials below and send them manually, or fix the email address and hit “Re-send invite”.
              </p>
            </div>
          )}
          <p className="mt-2 text-navy"><span className="text-ink-500 text-xs uppercase tracking-widest">Email:</span> <span className="font-mono">{revealed.email}</span></p>
          <p className="mt-1 text-navy"><span className="text-ink-500 text-xs uppercase tracking-widest">Password:</span> <span className="font-mono">{revealed.password}</span></p>
          <p className="mt-2 text-[11px] text-ink-500">They can sign in at /mlologin.</p>
          <button type="button" onClick={() => setRevealed(null)} className="mt-2 text-[11px] text-teal hover:underline">Dismiss</button>
        </div>
      )}

      {err && <p className="mt-3 text-xs text-red-600">{err}</p>}
    </div>
  );
}


function LeadsTable({ rows, clicksByClickId, onOpen, onChanged, showingArchived, onToggleArchived }) {
  const [picked, setPicked] = useState({});
  const [working, setWorking] = useState("");

  const ids = rows.map((r) => r.id).filter(Boolean);
  const selected = ids.filter((id) => picked[id]);
  const allOn = ids.length > 0 && selected.length === ids.length;

  const bulk = async (action) => {
    if (action === "delete") {
      const ok = window.confirm(
        `Permanently delete ${selected.length} lead${selected.length !== 1 ? "s" : ""}? ` +
          "This cannot be undone. Use Archive for real consumers you may need on record.",
      );
      if (!ok) return;
    }
    setWorking(action);
    try {
      const token = localStorage.getItem("grc_admin_token");
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/bulk`,
        { ids: selected, action },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        action === "delete"
          ? `Deleted ${data.count} lead${data.count !== 1 ? "s" : ""}`
          : action === "archive"
            ? `Archived ${data.count}`
            : `Restored ${data.count}`,
      );
      setPicked({});
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't work.");
    } finally {
      setWorking("");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 px-1 pb-3">
        <button
          onClick={onToggleArchived}
          data-testid="admin-leads-archived-toggle"
          className={`text-xs font-semibold rounded-full px-3.5 py-1.5 transition-colors ${
            showingArchived ? "bg-navy text-white" : "bg-white border border-linen-border text-ink-500 hover:text-navy"
          }`}
        >
          {showingArchived ? "Viewing archived" : "View archived"}
        </button>
        {selected.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="admin-leads-bulk-bar">
            <span className="text-xs text-ink-500">{selected.length} selected</span>
            {showingArchived ? (
              <button
                onClick={() => bulk("unarchive")}
                disabled={!!working}
                data-testid="admin-leads-bulk-unarchive"
                className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full bg-teal-light text-teal px-3.5 py-1.5 hover:bg-teal hover:text-white transition-colors disabled:opacity-50"
              >
                <ArchiveRestore size={13} /> Restore
              </button>
            ) : (
              <button
                onClick={() => bulk("archive")}
                disabled={!!working}
                data-testid="admin-leads-bulk-archive"
                className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full bg-linen text-navy px-3.5 py-1.5 hover:bg-linen-border transition-colors disabled:opacity-50"
              >
                <Archive size={13} /> Archive
              </button>
            )}
            <button
              onClick={() => bulk("delete")}
              disabled={!!working}
              data-testid="admin-leads-bulk-delete"
              className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full bg-red-50 text-red-700 border border-red-200 px-3.5 py-1.5 hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              {working === "delete" ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
              Delete permanently
            </button>
            <span className="text-[11px] text-ink-400">Archive keeps the record. Delete is for test rows.</span>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <Empty label={showingArchived ? "Nothing archived." : "No leads yet."} />
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-leads-table">
          <thead>
            <tr className="text-left text-ink-500 border-b border-linen-border bg-linen/50">
              <Th>
                <input
                  type="checkbox"
                  checked={allOn}
                  onChange={() => setPicked(allOn ? {} : Object.fromEntries(ids.map((id) => [id, true])))}
                  data-testid="admin-leads-select-all"
                  className="accent-[#1F8F8B]"
                />
              </Th>
              <Th>Name</Th><Th>Contact</Th><Th>Source</Th><Th>Owner</Th><Th>Stage</Th><Th>Next step</Th><Th>Last touch</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l, i) => (
              <tr
                key={l.id || i}
                onClick={() => onOpen && onOpen(l)}
                data-testid={`admin-lead-row-${l.id || i}`}
                className="border-b border-linen-border/60 hover:bg-linen/40 cursor-pointer"
              >
                <Td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={!!picked[l.id]}
                    onChange={() => setPicked((p) => ({ ...p, [l.id]: !p[l.id] }))}
                    data-testid={`admin-lead-pick-${l.id}`}
                    className="accent-[#1F8F8B]"
                  />
                </Td>
                <Td>
                  <span className="block font-medium text-navy">{l.full_name}</span>
                  <span className="block text-[11px] text-ink-500">{fmtDate(l.created_at)}</span>
                </Td>
                <Td className="text-ink-600 text-xs">
                  <span className="block">{l.email}</span>
                  <span className="block text-ink-500">{l.phone}</span>
                </Td>
                <Td>
                  {l.source === "apply_intent" ? (
                    <span className="inline-block text-[10px] uppercase tracking-widest bg-lime/20 text-navy px-2 py-0.5 rounded-md">
                      Apply-intent
                    </span>
                  ) : (
                    <span className="inline-block text-[10px] uppercase tracking-widest bg-teal/15 text-teal px-2 py-0.5 rounded-md">
                      Form
                    </span>
                  )}
                  {l.grc_code ? (
                    <span className="block mt-1 text-[10px] font-mono tracking-wider text-ink-500">{l.grc_code}</span>
                  ) : null}
                </Td>
                <Td>
                  {l.mlo_slug ? (
                    <span className="inline-block text-[11px] font-medium text-teal">/{l.mlo_slug}</span>
                  ) : (
                    <span className="inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-300" data-testid={`admin-lead-needs-officer-${l.id}`}>
                      Needs officer
                    </span>
                  )}
                </Td>
                <Td>
                  <StatusPill status={l.contact_status || "new"} />
                </Td>
                <Td className="whitespace-nowrap text-xs">
                  {l.next_action_on ? (
                    <span className={l.next_action_on <= new Date().toISOString().slice(0, 10) ? "text-red-600 font-semibold" : "text-ink-600"}>
                      {l.next_action_on}
                    </span>
                  ) : (
                    <span className="text-ink-400">—</span>
                  )}
                </Td>
                <Td className="whitespace-nowrap text-ink-500 text-xs">
                  {l.last_touch_at ? fmtDate(l.last_touch_at) : <span className="text-ink-400">never</span>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

// A handoff tracker, not a CRM: each stage says who owns it and what's next.
const STATUS_META = {
  new: { label: "New", cls: "bg-ink-100 text-ink-600 border border-linen-border", help: "Nobody owns this yet — assign an officer to start their clock." },
  handed_off: { label: "Handed off", cls: "bg-amber-100 text-amber-800", help: "An officer owns it. Mark connected once they actually reach them." },
  connected: { label: "Connected", cls: "bg-teal/15 text-teal", help: "The officer has spoken with them. Set a next step date." },
  application_started: { label: "Application started", cls: "bg-lime/25 text-navy", help: "Set automatically when they start a lender application." },
  closed: { label: "Closed", cls: "bg-navy text-white", help: "Done — funded, or definitively not moving forward." },
  nurture: { label: "Nurture", cls: "bg-linen text-ink-600 border border-linen-border", help: "Not now. Set a date to check back." },
  // legacy values, still rendered if an old record slips through
  contacted: { label: "Connected", cls: "bg-teal/15 text-teal", help: "" },
  qualified: { label: "Connected", cls: "bg-teal/15 text-teal", help: "" },
  no_response: { label: "Nurture", cls: "bg-linen text-ink-600 border border-linen-border", help: "" },
};
const LEAD_STAGES = ["new", "handed_off", "connected", "application_started", "closed", "nurture"];

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  return (
    <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function LeadDetail({ lead, mlos = [], clicksByClickId, onClose, onSaved }) {
  const [notes, setNotes] = useState(lead.follow_up_notes || "");
  const [status, setStatus] = useState(lead.contact_status || "new");
  const [nextAction, setNextAction] = useState(lead.next_action_on || "");
  const [assignee, setAssignee] = useState(lead.mlo_slug || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const token = localStorage.getItem("grc_admin_token");
      const res = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${lead.id}`,
        { follow_up_notes: notes, contact_status: status, mlo_slug: assignee, next_action_on: nextAction },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSaved && onSaved(res.data?.lead);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const parsedFromMsg = (() => {
    // Legacy fallback for pre-refactor apply-intent leads that stored ids in `message`
    if (lead.click_id || lead.source_button) return null;
    if (!lead.message) return null;
    const m = /click_id=([\w-]+).*?button=([\w-]+)/i.exec(lead.message);
    if (!m) return null;
    return { click_id: m[1], source_button: m[2] };
  })();

  const effectiveClickId = lead.click_id || parsedFromMsg?.click_id;
  const effectiveButton = lead.source_button || parsedFromMsg?.source_button;
  const matchedClick = effectiveClickId ? clicksByClickId?.[effectiveClickId] : null;

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/40 flex justify-end"
      data-testid="admin-lead-detail-backdrop"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg h-full bg-linen border-l border-linen-border shadow-2xl overflow-y-auto"
        data-testid="admin-lead-detail-panel"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-linen-border px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-teal">Lead detail</p>
            <h3 className="mt-1 font-heading font-medium text-xl text-navy truncate">{lead.full_name}</h3>
            <div className="mt-2 flex items-center gap-2">
              {lead.source === "apply_intent" ? (
                <span className="inline-block text-[10px] uppercase tracking-widest bg-lime/20 text-navy px-2 py-0.5 rounded-md">Apply-intent</span>
              ) : (
                <span className="inline-block text-[10px] uppercase tracking-widest bg-teal/15 text-teal px-2 py-0.5 rounded-md">Form</span>
              )}
              <StatusPill status={status} />
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-lead-detail-close"
            className="p-2 rounded-lg hover:bg-linen text-ink-500"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Contact */}
          <section>
            <h4 className="text-xs uppercase tracking-widest text-ink-500 mb-2">Contact</h4>
            <dl className="grid grid-cols-3 gap-y-2 text-sm">
              <dt className="text-ink-500">Email</dt>
              <dd className="col-span-2 text-navy">
                <a href={`mailto:${lead.email}`} className="underline decoration-linen-border underline-offset-4 hover:decoration-teal">
                  {lead.email}
                </a>
              </dd>
              <dt className="text-ink-500">Phone</dt>
              <dd className="col-span-2 text-navy">
                <a href={`tel:${lead.phone}`} className="underline decoration-linen-border underline-offset-4 hover:decoration-teal">
                  {lead.phone}
                </a>
              </dd>
              {lead.state && (<><dt className="text-ink-500">State</dt><dd className="col-span-2 text-navy">{lead.state}</dd></>)}
              {lead.loan_amount && (<><dt className="text-ink-500">Amount</dt><dd className="col-span-2 text-navy">{lead.loan_amount}</dd></>)}
              {lead.loan_purpose && (<><dt className="text-ink-500">Purpose</dt><dd className="col-span-2 text-navy">{lead.loan_purpose}</dd></>)}
            </dl>
          </section>

          {/* Origin trail */}
          <section>
            <h4 className="text-xs uppercase tracking-widest text-ink-500 mb-2">Origin trail</h4>
            <div className="rounded-2xl bg-white border border-linen-border p-4 text-sm space-y-2">
              {effectiveButton && (
                <div className="flex items-start gap-3">
                  <span className="text-ink-500 shrink-0 w-24">Button</span>
                  <code className="text-navy font-mono text-xs bg-linen px-2 py-0.5 rounded">{effectiveButton}</code>
                </div>
              )}
              {effectiveClickId && (
                <div className="flex items-start gap-3">
                  <span className="text-ink-500 shrink-0 w-24">Click ID</span>
                  <code className="text-navy font-mono text-[10px] break-all bg-linen px-2 py-0.5 rounded">{effectiveClickId}</code>
                </div>
              )}
              {matchedClick?.referrer && (
                <div className="flex items-start gap-3">
                  <span className="text-ink-500 shrink-0 w-24">Referrer</span>
                  <span className="text-navy break-all text-xs">{matchedClick.referrer}</span>
                </div>
              )}
              {matchedClick?.landing_path && (
                <div className="flex items-start gap-3">
                  <span className="text-ink-500 shrink-0 w-24">Landing</span>
                  <code className="text-navy font-mono text-xs">{matchedClick.landing_path}</code>
                </div>
              )}
              {matchedClick?.utm_source && (
                <div className="flex items-start gap-3">
                  <span className="text-ink-500 shrink-0 w-24">UTM source</span>
                  <span className="text-navy text-xs">{matchedClick.utm_source}</span>
                </div>
              )}
              {matchedClick?.utm_campaign && (
                <div className="flex items-start gap-3">
                  <span className="text-ink-500 shrink-0 w-24">UTM campaign</span>
                  <span className="text-navy text-xs">{matchedClick.utm_campaign}</span>
                </div>
              )}
              {matchedClick?.country && (
                <div className="flex items-start gap-3">
                  <span className="text-ink-500 shrink-0 w-24">Country</span>
                  <span className="text-navy text-xs">{matchedClick.country}</span>
                </div>
              )}
              {matchedClick?.user_agent && (
                <div className="flex items-start gap-3">
                  <span className="text-ink-500 shrink-0 w-24">Device</span>
                  <span className="text-navy text-xs break-all">{matchedClick.user_agent}</span>
                </div>
              )}
              {!effectiveButton && !effectiveClickId && !matchedClick && (
                <p className="text-ink-500 text-xs italic">Direct form submission — no handoff trail.</p>
              )}
            </div>
          </section>

          {/* Message from lead */}
          {lead.message && !parsedFromMsg && (
            <section>
              <h4 className="text-xs uppercase tracking-widest text-ink-500 mb-2">Their message</h4>
              <p className="rounded-2xl bg-white border border-linen-border p-4 text-sm text-navy whitespace-pre-wrap">{lead.message}</p>
            </section>
          )}

          {/* Stage + next step */}
          <section>
            <h4 className="text-xs uppercase tracking-widest text-ink-500 mb-2">Stage</h4>
            <div className="flex flex-wrap gap-2">
              {LEAD_STAGES.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  data-testid={`admin-lead-status-${s}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                    status === s
                      ? "bg-navy text-white shadow"
                      : "bg-white border border-linen-border text-ink-600 hover:border-teal"
                  }`}
                >
                  {STATUS_META[s].label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-ink-500 leading-relaxed" data-testid="admin-lead-stage-help">
              {(STATUS_META[status] || STATUS_META.new).help}
            </p>
            <div className="mt-4">
              <h4 className="text-xs uppercase tracking-widest text-ink-500 mb-2">Next step due</h4>
              <input
                type="date"
                value={nextAction}
                onChange={(e) => setNextAction(e.target.value)}
                data-testid="admin-lead-next-action"
                className="rounded-xl border border-linen-border p-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal/40"
              />
              {nextAction ? (
                <button
                  onClick={() => setNextAction("")}
                  data-testid="admin-lead-next-action-clear"
                  className="ml-3 text-[11px] text-ink-500 hover:text-navy"
                >
                  Clear
                </button>
              ) : null}
              <p className="mt-1 text-[11px] text-ink-500">
                When this date arrives, the lead shows up on your Today screen.
              </p>
            </div>
          </section>

          {/* Assign to a loan officer */}
          <section>
            <h4 className="text-xs uppercase tracking-widest text-ink-500 mb-2">Assigned loan officer</h4>
            <select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              data-testid="admin-lead-assign"
              className="w-full rounded-xl border border-linen-border p-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal/40"
            >
              <option value="">Unassigned — house lead</option>
              {mlos.map((m) => (
                <option key={m.slug} value={m.slug}>
                  {m.name} (/{m.slug}){(m.licensed_states || []).length ? ` — ${(m.licensed_states || []).join(", ")}` : ""}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-ink-500 mt-1">
              {lead.property_state
                ? `Property state: ${lead.property_state}${lead.property_zip ? ` ${lead.property_zip}` : ""}. `
                : ""}
              Saving emails the officer this lead's details right away.
            </p>
          </section>

          {/* Follow-up notes */}
          <section>
            <h4 className="text-xs uppercase tracking-widest text-ink-500 mb-2">Follow-up notes</h4>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={6}
              placeholder="e.g. Called Tuesday 3pm — left voicemail. Sent rate comparison via text. Follow up Friday."
              data-testid="admin-lead-notes-input"
              className="w-full rounded-2xl border border-linen-border focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 p-4 text-sm text-navy bg-white"
            />
            <p className="mt-1 text-[11px] text-ink-500">
              Visible to admins only. Add a running log of each touchpoint.
            </p>
          </section>

          {/* Timestamps */}
          <section className="text-[11px] text-ink-500 space-y-1">
            <p>Created {fmtDate(lead.created_at)}</p>
            {lead.updated_at && (
              <p>Last updated {fmtDate(lead.updated_at)}{lead.updated_by ? ` · ${lead.updated_by}` : ""}</p>
            )}
          </section>

          {err && <p className="text-sm text-red-600" data-testid="admin-lead-detail-error">{err}</p>}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 bg-white border-t border-linen-border px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full text-navy hover:bg-linen"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="admin-lead-detail-save"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-navy hover:bg-[#173a5f] text-white disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

function ClicksTable({ rows }) {
  if (rows.length === 0) return <Empty label="No apply-clicks yet." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-clicks-table">
        <thead>
          <tr className="text-left text-ink-500 border-b border-linen-border bg-linen/50">
            <Th>Source</Th><Th>Campaign</Th><Th>Referrer</Th><Th>Landing</Th><Th>Country</Th><Th>When</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c, i) => (
            <tr key={i} className="border-b border-linen-border/60 hover:bg-linen/40">
              <Td className="font-medium text-navy">{c.source || "—"}</Td>
              <Td>{c.utm_source || c.utm_campaign || "direct"}</Td>
              <Td className="max-w-[220px] truncate">{c.referrer || "—"}</Td>
              <Td className="max-w-[200px] truncate">{c.landing_path || "—"}</Td>
              <Td>{c.country || "—"}</Td>
              <Td className="whitespace-nowrap text-ink-500">{fmtDate(c.created_at)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-semibold uppercase text-xs tracking-wider">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 text-ink-700 ${className}`}>{children}</td>;
const Empty = ({ label }) => <div className="py-16 text-center text-ink-400 text-sm">{label}</div>;

const CATEGORY_LABEL = {
  marketing: "Marketing",
  messaging: "Messaging",
  process: "Process",
  other: "Other",
};

function fmtBytes(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function LibraryDownloadsTable({ rows, totalDownloads }) {
  if (rows.length === 0)
    return <Empty label="No library assets yet. Upload some in /library." />;
  return (
    <div>
      <div className="px-4 py-3 border-b border-linen-border bg-linen/40 text-xs text-ink-500 uppercase tracking-wider flex items-center justify-between">
        <span>Assets ({rows.length})</span>
        <span>
          Total downloads: <span className="text-navy font-semibold tabular-nums">{totalDownloads}</span>
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-library-table">
          <thead>
            <tr className="text-left text-ink-500 border-b border-linen-border bg-linen/50">
              <Th>File</Th>
              <Th>Category</Th>
              <Th>Downloads</Th>
              <Th>Size</Th>
              <Th>Uploaded</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a, i) => (
              <tr key={a.id || i} className="border-b border-linen-border/60 hover:bg-linen/40" data-testid={`admin-library-row-${i}`}>
                <Td className="font-medium text-navy max-w-[340px]">
                  <div className="truncate" title={a.filename}>{a.filename}</div>
                  {a.description ? (
                    <div className="text-xs text-ink-500 truncate mt-0.5" title={a.description}>{a.description}</div>
                  ) : null}
                </Td>
                <Td>
                  <span className="inline-block text-[11px] uppercase tracking-widest bg-linen border border-linen-border rounded-full px-2 py-0.5">
                    {CATEGORY_LABEL[a.category] || a.category}
                  </span>
                </Td>
                <Td className="tabular-nums">
                  <span
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 ${
                      (a.download_count || 0) > 0
                        ? "bg-teal/10 text-teal font-semibold"
                        : "text-ink-400"
                    }`}
                    data-testid={`admin-library-downloads-${i}`}
                  >
                    <Download size={12} /> {a.download_count || 0}
                  </span>
                </Td>
                <Td className="text-ink-500 tabular-nums">{fmtBytes(a.size)}</Td>
                <Td className="whitespace-nowrap text-ink-500">{fmtDate(a.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReportsList({ rows, openFile, downloadReportPDF }) {
  const [open, setOpen] = useState(null);
  if (rows.length === 0)
    return (
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft">
        <Empty label="No verification reports submitted yet." />
      </div>
    );
  return (
    <div className="space-y-3" data-testid="admin-reports-list">
      {rows.map((r, i) => {
        const isOpen = open === (r.id || i);
        return (
          <div key={r.id || i} data-testid={`admin-report-${i}`} className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
            <button
              onClick={() => setOpen(isOpen ? null : r.id || i)}
              className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-linen/40 transition-colors"
            >
              <div className="min-w-0">
                <p className="font-heading font-medium text-navy truncate">
                  {r.bdm_name}{" "}
                  <span className="text-xs font-body text-ink-400">· {r.role}</span>
                </p>
                <p className="text-xs text-ink-500 mt-0.5">
                  {r.period_label} · LO: {r.loan_officer_name} · {(r.uploads || []).length} file{(r.uploads || []).length === 1 ? "" : "s"}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-ink-400 hidden sm:inline">{fmtDate(r.created_at)}</span>
                <span className={`text-xs px-2.5 py-1 rounded-full ${r.certified ? "bg-lime-soft text-navy-deep" : "bg-linen text-ink-500"}`}>
                  {r.certified ? "Certified" : "Unsigned"}
                </span>
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-linen-border px-5 py-5 space-y-5 bg-linen/30" data-testid={`admin-report-detail-${i}`}>
                <div className="flex justify-end">
                  <button
                    data-testid={`admin-report-pdf-${i}`}
                    onClick={() => downloadReportPDF(r)}
                    className="inline-flex items-center gap-2 text-sm font-medium bg-navy text-white px-4 py-2 rounded-full hover:bg-navy-light transition-colors"
                  >
                    <Download size={15} /> Download PDF
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <Detail label="Email" value={r.email} />
                  <Detail label="Signature" value={r.signature} />
                  <Detail label="Scope of work" value={(r.scope_of_work || []).join(", ") || "—"} full />
                  {r.scope_other ? <Detail label="Other scope" value={r.scope_other} full /> : null}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    ["Campaigns", r.activities?.campaigns],
                    ["Social media posts", r.activities?.social_media_posts],
                    ["Events", r.activities?.events],
                    ["Materials created", r.activities?.materials_created],
                    ["Partner promotion", r.activities?.partner_promotion],
                  ].map(([label, val]) => (
                    <div key={label} className="rounded-xl bg-white border border-linen-border p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">{label}</p>
                      <p className="text-sm text-navy mt-1 whitespace-pre-wrap">{val || "—"}</p>
                    </div>
                  ))}
                </div>

                {r.additional_notes ? (
                  <div className="rounded-xl bg-white border border-linen-border p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">Additional notes</p>
                    <p className="text-sm text-navy mt-1 whitespace-pre-wrap">{r.additional_notes}</p>
                  </div>
                ) : null}

                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-2">
                    Supporting files ({(r.uploads || []).length})
                  </p>
                  {(r.uploads || []).length === 0 ? (
                    <p className="text-sm text-ink-400">No files attached.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {r.uploads.map((u, ui) => (
                        <button
                          key={u.file_id}
                          data-testid={`admin-report-file-${i}-${ui}`}
                          onClick={() => openFile(u.file_id)}
                          className="inline-flex items-center gap-2 rounded-lg bg-white border border-linen-border px-3 py-2 text-xs text-navy hover:border-teal hover:shadow-soft transition-all max-w-[240px]"
                          title={`${u.filename} · ${u.category || ""}`}
                        >
                          {u.content_type === "application/pdf" ? <FileText size={14} className="text-teal shrink-0" /> : <ImageIcon size={14} className="text-teal shrink-0" />}
                          <span className="truncate">{u.filename}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Detail({ label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">{label}: </span>
      <span className="text-navy">{value || "—"}</span>
    </div>
  );
}



const TIER_STYLE = {
  preferred: "bg-lime-soft text-navy-deep border border-[#A6D24A]",
  qualified: "bg-teal/15 text-teal border border-teal/40",
  developmental: "bg-amber-100 text-amber-800 border border-amber-300",
  emerging: "bg-linen text-ink-500 border border-linen-border",
};
const STATUS_STYLE = {
  new: "bg-teal/15 text-teal border border-teal/40",
  under_review: "bg-amber-100 text-amber-800 border border-amber-300",
  approved: "bg-lime-soft text-navy-deep border border-[#A6D24A]",
  developmental: "bg-[#0F2A47]/10 text-[#0F2A47] border border-[#0F2A47]/30",
  not_approved: "bg-red-100 text-red-800 border border-red-300",
};

function ApplicationsTable({ rows, onRefresh }) {
  const [openApp, setOpenApp] = useState(null);
  const [mlos, setMlos] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem("grc_admin_token");
    if (!token) return;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/mlos`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setMlos(r.data || []))
      .catch(() => setMlos([]));
  }, []);
  if (rows.length === 0) return <Empty label="No Partner applications yet." />;
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-applications-table">
          <thead>
            <tr className="text-left text-ink-500 border-b border-linen-border bg-linen/50">
              <Th>Applicant</Th>
              <Th>Type</Th>
              <Th>Score</Th>
              <Th>Tier</Th>
              <Th>Status</Th>
              <Th>Company</Th>
              <Th>Submitted</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a, i) => (
              <tr key={a.id || i} className="border-b border-linen-border/60 hover:bg-linen/40" data-testid={`admin-application-row-${i}`}>
                <Td>
                  <div className="font-medium text-navy">{a.full_name}</div>
                  <div className="text-xs text-ink-500">{a.email}</div>
                </Td>
                <Td>
                  {a.application_type === "mlo" ? (
                    <span className="inline-block text-[10px] uppercase tracking-widest bg-lime/25 text-navy border border-lime rounded-full px-2 py-0.5">MLO</span>
                  ) : (
                    <span className="inline-block text-[10px] uppercase tracking-widest bg-teal/15 text-teal border border-teal/40 rounded-full px-2 py-0.5">BDM</span>
                  )}
                </Td>
                <Td className="tabular-nums font-semibold text-navy">
                  {a.score_total}<span className="text-ink-400 font-normal">/100</span>
                </Td>
                <Td>
                  <span className={`inline-block text-[11px] uppercase tracking-widest rounded-full px-2 py-0.5 ${TIER_STYLE[a.classification] || TIER_STYLE.emerging}`}>
                    {a.classification || "—"}
                  </span>
                </Td>
                <Td>
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-block text-[11px] uppercase tracking-widest rounded-full px-2 py-0.5 ${STATUS_STYLE[a.status] || STATUS_STYLE.new}`}>
                      {(a.status || "new").replace(/_/g, " ")}
                    </span>
                    {!a.reviewed_by && (
                      <span
                        data-testid={`admin-application-auto-badge-${i}`}
                        title="Automatically set by score — not yet reviewed by a human"
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-1.5 py-0.5"
                      >
                        Auto
                      </span>
                    )}
                  </div>
                </Td>
                <Td className="text-ink-700 max-w-[220px]">
                  <div className="truncate">{a.company || "—"}</div>
                  {a.referring_mlo_slug ? (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-lime/20 text-navy border border-lime/40 rounded-full px-1.5 py-0.5">
                      via /{a.referring_mlo_slug}
                    </div>
                  ) : (a.application_type || "bdm") === "bdm" ? (
                    <div
                      data-testid={`admin-application-unassigned-${i}`}
                      title="Free-agent BDM — no referring MLO. Assign one from the Review modal."
                      className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-1.5 py-0.5"
                    >
                      Unassigned — needs routing
                    </div>
                  ) : null}
                  <div className="text-xs text-ink-500 truncate">{a.city_state || ""}</div>
                </Td>
                <Td className="whitespace-nowrap text-ink-500">{fmtDate(a.created_at)}</Td>
                <Td>
                  <button
                    onClick={() => setOpenApp(a)}
                    data-testid={`admin-application-open-${i}`}
                    className="text-xs font-medium text-teal hover:underline"
                  >
                    Review →
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openApp && (
        <ApplicationDetail
          app={openApp}
          mlos={mlos}
          onClose={() => setOpenApp(null)}
          onSaved={() => { setOpenApp(null); onRefresh && onRefresh(); }}
        />
      )}
    </>
  );
}

function ApplicationDetail({ app, mlos = [], onClose, onSaved }) {
  const [status, setStatus] = useState(app.status || "new");
  const [notes, setNotes] = useState(app.review_notes || "");
  const [assignSlug, setAssignSlug] = useState(app.referring_mlo_slug || "");
  const [notifyMlo, setNotifyMlo] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [assignMsg, setAssignMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [err, setErr] = useState("");

  const isBDM = (app.application_type || "bdm") === "bdm";
  const currentAssignment = app.referring_mlo_slug || "";
  const canAssign = isBDM && assignSlug !== currentAssignment;

  const doAssign = async () => {
    setAssigning(true);
    setErr("");
    setAssignMsg("");
    try {
      const token = localStorage.getItem("grc_admin_token");
      const { data } = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bdm-applications/${app.id}/assign-mlo`,
        { mlo_slug: assignSlug || null, notify_mlo: notifyMlo },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.notified) {
        setAssignMsg(`Assigned to /${data.referring_mlo_slug} and notified via email.`);
      } else if (data.referring_mlo_slug) {
        setAssignMsg(`Assigned to /${data.referring_mlo_slug} (no email sent).`);
      } else {
        setAssignMsg("Unassigned — this application is back in the free-agent pool.");
      }
      // Reflect on the current app in place so subsequent edits don't re-fire
      app.referring_mlo_slug = data.referring_mlo_slug;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Assign failed.");
    } finally {
      setAssigning(false);
    }
  };
  const downloadPdf = async () => {
    setDownloadingPdf(true);
    setErr("");
    try {
      const token = localStorage.getItem("grc_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bdm-applications/${app.id}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (app.full_name || "applicant").replace(/[^A-Za-z0-9._-]+/g, "-");
      a.download = `GRC-Partner-Application-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr("Could not download PDF.");
    } finally {
      setDownloadingPdf(false);
    }
  };
  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const token = localStorage.getItem("grc_admin_token");
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bdm-applications/${app.id}`, {
        status,
        review_notes: notes,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const tierLabel = {
    preferred: "Preferred Partner (80–100)",
    qualified: "Qualified Partner (65–79)",
    developmental: "Developmental Partner (50–64)",
    emerging: "Emerging / Self-Service (<50)",
  }[app.classification] || app.classification;

  const kv = (label, v) => (
    <div className="text-sm">
      <div className="text-[10px] uppercase tracking-widest text-ink-500">{label}</div>
      <div className="text-navy mt-0.5 break-words">{(v && String(v).trim()) || "—"}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-application-detail">
      <div
        className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 bg-[#0F2A47] text-white flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-[#A6D24A]">Partner Application</div>
            <div className="text-xl font-semibold">{app.full_name}</div>
            <div className="text-xs text-white/70">{app.email} · {app.mobile_phone}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-widest uppercase text-[#A6D24A]">Internal Score</div>
            <div className="text-3xl font-bold">{app.score_total}<span className="text-white/50 text-base">/100</span></div>
            <div className="text-xs text-white/80">{tierLabel}</div>
          </div>
        </div>

        <div className="overflow-y-auto p-6 space-y-6">
          {/* Score breakdown */}
          <div>
            <h3 className="text-sm font-semibold text-navy mb-2">Score breakdown</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {Object.entries(app.score_breakdown || {}).map(([k, v]) => (
                <div key={k} className="rounded-lg bg-linen/60 border border-linen-border px-3 py-2">
                  <div className="text-[10px] uppercase tracking-widest text-ink-500">{k.replace(/_/g, " ")}</div>
                  <div className="text-lg font-semibold text-navy tabular-nums">{v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact & business */}
          <div>
            <h3 className="text-sm font-semibold text-navy mb-2">Contact & business</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {kv("Company / Brokerage", app.company)}
              {kv("City / State", app.city_state)}
              {kv("RE License", app.real_estate_license)}
              {kv("GRC Affiliation", app.grc_affiliation)}
              {kv("If yes, who", app.grc_affiliation_with)}
              {kv("Referred By", app.referred_by)}
              {kv("Years in RE", app.q1_years_in_re)}
              {kv("Transactions (12mo)", app.q2_transactions_12mo)}
              {kv("On team", app.q3_on_team + (app.q3_on_team === "yes" ? ` (${app.q3_team_size})` : ""))}
              {kv("Business type", app.q4_business_type)}
            </div>
          </div>

          {/* Database & Outreach */}
          <div>
            <h3 className="text-sm font-semibold text-navy mb-2">Database & Outreach</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {kv("Sphere size", app.q5_sphere_size)}
              {kv("CRM usage", app.q6_uses_crm)}
              {kv("CRM frequency", app.q7_crm_frequency)}
              {kv("CRM name", app.q8_crm_name)}
              {kv("Outreach frequency", app.q9_outreach_frequency)}
              {kv("Conversations/month", app.q10_conversations_per_month)}
              {kv("Outreach forms", (app.q11_outreach_forms || []).join(", "))}
              {kv("Consistency", app.q12_consistency)}
            </div>
          </div>

          {/* Program Fit */}
          <div>
            <h3 className="text-sm font-semibold text-navy mb-2">Program fit</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {kv("Approach", app.q14_approach)}
              {kv("Willing: Onboarding", app.q15_willing_onboarding)}
              {kv("Willing: Meetings", app.q16_willing_meetings)}
              {kv("Willing: Messaging", app.q17_willing_messaging)}
              {kv("Willing: Records", app.q18_willing_records)}
              {kv("Willing: Goals", app.q19_willing_goals)}
              {kv("Coachability", app.q20_coachability)}
            </div>
          </div>

          {/* Narrative */}
          <div>
            <h3 className="text-sm font-semibold text-navy mb-2">Why they want to join</h3>
            <div className="rounded-lg bg-linen/60 border border-linen-border p-3 text-sm text-navy whitespace-pre-wrap">
              {app.q13_primary_reason || "—"}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-navy mb-2">Additional strengths (freeform)</h3>
            <div className="rounded-lg bg-linen/60 border border-linen-border p-3 text-sm text-navy whitespace-pre-wrap">
              {app.q22_freeform_strengths || "—"}
            </div>
            <div className="mt-2 text-xs text-ink-500">
              <strong>Selected:</strong> {(app.q21_strengths || []).join(", ") || "—"}
            </div>
          </div>

          {/* Agreements */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-semibold text-navy mb-2">Section 7 agreements</h3>
              <div className="text-sm text-navy">{(app.agreements_section7 || []).length} / 10 agreed</div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-navy mb-2">Section 8 (90-day activation)</h3>
              <div className="text-sm text-navy">{(app.agreements_section8 || []).length} / 7 committed</div>
            </div>
          </div>

          {/* Signature */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {kv("Applicant name", app.applicant_name)}
            {kv("Signature", app.signature)}
            {kv("Date signed", app.signed_date)}
          </div>

          {/* MLO assignment (BDM apps only) */}
          {isBDM && (
            <div className="border-t border-linen-border pt-4" data-testid="admin-application-assign-section">
              <h3 className="text-sm font-semibold text-navy mb-1">Referring MLO</h3>
              <p className="text-xs text-ink-500 mb-3">
                {currentAssignment
                  ? <>Currently attributed to <code className="bg-linen px-1.5 py-0.5 rounded">/{currentAssignment}</code>. Reassign below if this BDM should belong to a different officer.</>
                  : "This BDM came in as a free agent. Assign them to an MLO so the referral chain is complete."}
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <select
                  value={assignSlug}
                  onChange={(e) => setAssignSlug(e.target.value)}
                  data-testid="admin-application-assign-select"
                  className="flex-1 rounded-xl border border-linen-border focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 p-2.5 text-sm text-navy bg-white"
                >
                  <option value="">— Unassigned (free agent) —</option>
                  {mlos.filter((m) => m.active !== false).map((m) => (
                    <option key={m.slug} value={m.slug}>{m.name} (/{m.slug})</option>
                  ))}
                </select>
                <label className="inline-flex items-center gap-1.5 text-xs text-ink-600 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={notifyMlo}
                    onChange={(e) => setNotifyMlo(e.target.checked)}
                    data-testid="admin-application-assign-notify"
                    className="accent-teal"
                  />
                  Email the MLO
                </label>
                <button
                  onClick={doAssign}
                  disabled={!canAssign || assigning}
                  data-testid="admin-application-assign-save"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-teal hover:bg-teal-hover text-white font-medium text-sm px-4 py-2.5 disabled:opacity-60"
                >
                  {assigning && <Loader2 className="w-4 h-4 animate-spin" />}
                  {assignSlug ? (currentAssignment ? "Reassign" : "Assign") : "Unassign"}
                </button>
              </div>
              {assignMsg && (
                <p data-testid="admin-application-assign-msg" className="mt-2 text-xs text-teal">
                  {assignMsg}
                </p>
              )}
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-preview/bdm-assigned`}
                target="_blank"
                rel="noreferrer"
                data-testid="admin-application-assign-preview-email"
                className="mt-2 inline-flex items-center gap-1 text-[11px] text-teal hover:underline"
              >
                Preview what the MLO will receive →
              </a>
              {app.assigned_by_admin && (
                <p className="mt-1.5 text-[11px] text-ink-500">
                  Last routing by {app.assigned_by_admin} · {fmtDate(app.assigned_at)}
                </p>
              )}
            </div>
          )}

          {/* Review controls */}
          <div className="border-t border-linen-border pt-4">
            <h3 className="text-sm font-semibold text-navy mb-2">Review decision</h3>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-3">
              {[
                ["new", "New"],
                ["under_review", "Under Review"],
                ["approved", "Approved"],
                ["developmental", "Developmental"],
                ["not_approved", "Not Approved"],
              ].map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setStatus(v)}
                  data-testid={`admin-application-status-${v}`}
                  className={`text-xs px-3 py-2 rounded-full border transition ${
                    status === v
                      ? `${STATUS_STYLE[v]} font-semibold`
                      : "bg-white border-linen-border text-ink-500 hover:border-teal hover:text-teal"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Notes, exception reason, or context for this decision…"
              className="w-full rounded-xl border border-linen-border focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 p-3 text-sm text-navy"
              data-testid="admin-application-notes"
            />
            {app.reviewed_by ? (
              <p className="text-[11px] text-ink-500 mt-2">
                Last reviewed by {app.reviewed_by} · {fmtDate(app.reviewed_at)}
              </p>
            ) : (
              <p
                data-testid="admin-application-auto-note"
                className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5"
                title="Automatically set by score — not yet reviewed by a human"
              >
                Auto-tagged · not yet human reviewed
              </p>
            )}
            {err && <p className="text-sm text-red-600 mt-2">{err}</p>}
          </div>
        </div>

        <div className="px-6 py-4 bg-linen/40 border-t border-linen-border flex items-center justify-between gap-3">
          <button
            onClick={downloadPdf}
            disabled={downloadingPdf}
            data-testid="admin-application-pdf"
            className="inline-flex items-center gap-2 text-sm bg-white hover:bg-linen text-navy border border-linen-border px-4 py-2 rounded-full disabled:opacity-60"
          >
            {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download PDF
          </button>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-navy hover:bg-linen">Close</button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="admin-application-save"
              className="inline-flex items-center gap-2 text-sm bg-navy hover:bg-navy-light text-white px-5 py-2 rounded-full disabled:opacity-60"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save decision
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- BDM CRM Panel ----------------------------- */
const TIER_ORDER = ["preferred", "qualified", "developmental", "emerging"];

function BDMsPanel({ bdms, q }) {
  const [tierFilter, setTierFilter] = useState("all");
  const [mloFilter, setMloFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [activeBdm, setActiveBdm] = useState(null);
  const [showIssue, setShowIssue] = useState(false);
  const [onboarding, setOnboarding] = useState({});
  const [obBusy, setObBusy] = useState("");

  const loadOnboarding = useCallback(() => {
    api
      .get("/admin/onboarding/bdms")
      .then((r) => {
        const map = {};
        for (const row of r.data || []) map[row.email] = row;
        setOnboarding(map);
      })
      .catch(() => setOnboarding({}));
  }, []);
  useEffect(() => { loadOnboarding(); }, [loadOnboarding]);

  const sendLink = async (email) => {
    setObBusy(email);
    try {
      const { data } = await api.post(`/admin/onboarding/bdms/${encodeURIComponent(email)}/link`);
      toast.success(`Checklist link emailed to ${email}`);
      copyToClipboard(data.link);
      loadOnboarding();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the link.");
    } finally {
      setObBusy("");
    }
  };

  const nudgePartner = async (email) => {
    setObBusy(email);
    try {
      const { data } = await api.post(`/admin/onboarding/bdms/${encodeURIComponent(email)}/nudge`);
      toast.success(`Nudged ${email} — stuck on "${data.stuck_label}"`);
      loadOnboarding();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the nudge.");
    } finally {
      setObBusy("");
    }
  };

  const mloOptions = useMemo(() => {
    const map = {};
    for (const b of bdms) {
      if (b.referring_mlo_slug) map[b.referring_mlo_slug] = b.referring_mlo_name || b.referring_mlo_slug;
    }
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [bdms]);

  const tierCounts = useMemo(() => {
    const c = { all: bdms.length };
    for (const t of TIER_ORDER) c[t] = bdms.filter((b) => b.classification === t).length;
    return c;
  }, [bdms]);

  const filtered = useMemo(() => {
    let list = bdms;
    if (tierFilter !== "all") list = list.filter((b) => b.classification === tierFilter);
    if (mloFilter === "unassigned") list = list.filter((b) => !b.referring_mlo_slug);
    else if (mloFilter !== "all") list = list.filter((b) => b.referring_mlo_slug === mloFilter);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((b) =>
        [b.full_name, b.email, b.company, b.city_state, b.referring_mlo_name]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(s))
      );
    }
    return list;
  }, [bdms, tierFilter, mloFilter, q]);

  const toggleOne = (email) =>
    setSelected((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));
  const allVisibleSelected = filtered.length > 0 && filtered.every((b) => selected.includes(b.email));
  const toggleAll = () =>
    setSelected(allVisibleSelected ? selected.filter((e) => !filtered.some((b) => b.email === e)) : [
      ...new Set([...selected, ...filtered.map((b) => b.email)]),
    ]);

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="admin-bdms-tier-filter">
        <span className="text-xs uppercase tracking-widest text-ink-500 mr-1">Tier:</span>
        {[["all", "All"], ...TIER_ORDER.map((t) => [t, t.charAt(0).toUpperCase() + t.slice(1)])].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTierFilter(key)}
            data-testid={`admin-bdms-tier-${key}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
              tierFilter === key ? "bg-navy text-white shadow" : "bg-white border border-linen-border text-ink-600 hover:border-teal"
            }`}
          >
            {label}
            <span className={`ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded-full ${tierFilter === key ? "bg-white/20" : "bg-linen"}`}>
              {tierCounts[key] ?? 0}
            </span>
          </button>
        ))}
        <span className="text-xs uppercase tracking-widest text-ink-500 ml-3 mr-1">MLO:</span>
        <select
          data-testid="admin-bdms-mlo-filter"
          value={mloFilter}
          onChange={(e) => setMloFilter(e.target.value)}
          className="py-1.5 px-3 rounded-full bg-white border border-linen-border text-xs focus:outline-none focus:ring-2 focus:ring-teal/40"
        >
          <option value="all">All MLOs</option>
          <option value="unassigned">Unassigned</option>
          {mloOptions.map(([slug, name]) => (
            <option key={slug} value={slug}>{name}</option>
          ))}
        </select>
      </div>

      {selected.length > 0 && (
        <div
          data-testid="admin-bdms-bulk-bar"
          className="mb-3 rounded-2xl bg-navy text-white px-4 py-3 flex flex-wrap items-center justify-between gap-3"
        >
          <span className="text-sm">
            <strong className="tabular-nums">{selected.length}</strong> partner{selected.length !== 1 ? "s" : ""} selected
            {selected.length > 25 && <span className="ml-2 text-amber-300 text-xs">(max 25 per send)</span>}
          </span>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-bdms-bulk-issue"
              onClick={() => setShowIssue(true)}
              disabled={selected.length > 25}
              className="inline-flex items-center gap-2 text-sm font-medium bg-lime text-navy-deep px-4 py-2 rounded-full hover:brightness-105 transition disabled:opacity-50"
            >
              <Send size={14} /> Issue folder to {selected.length}
            </button>
            <button
              data-testid="admin-bdms-clear-selection"
              onClick={() => setSelected([])}
              className="text-xs text-white/70 hover:text-white underline"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-bdms-table">
            <thead className="bg-linen text-left text-ink-500">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    data-testid="admin-bdms-select-all"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    className="accent-teal"
                  />
                </th>
                <Th>Partner</Th>
                <Th>Company</Th>
                <Th>Tier / Score</Th>
                <Th>Assigned MLO</Th>
                <Th>First week</Th>
                <Th>Packets</Th>
                <Th>Reports</Th>
                <Th>Last activity</Th>
                <Th>{""}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-linen-border">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-ink-400" data-testid="admin-bdms-empty">
                    No approved partners yet. Approve a Partner application and it appears here automatically.
                  </td>
                </tr>
              ) : (
                filtered.map((b) => (
                  <tr key={b.email} className="hover:bg-linen/50 transition-colors" data-testid={`admin-bdm-row-${b.email}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.includes(b.email)}
                        onChange={() => toggleOne(b.email)}
                        data-testid={`admin-bdm-check-${b.email}`}
                        className="accent-teal"
                      />
                    </td>
                    <Td>
                      <div className="font-medium text-navy">{b.full_name || "—"}</div>
                      <div className="text-xs text-ink-500">{b.email}</div>
                    </Td>
                    <Td>
                      <div>{b.company || "—"}</div>
                      <div className="text-xs text-ink-400">{b.city_state || ""}</div>
                    </Td>
                    <Td>
                      <span className={`inline-block text-[11px] uppercase tracking-widest rounded-full px-2 py-0.5 ${TIER_STYLE[b.classification] || TIER_STYLE.emerging}`}>
                        {b.classification || "—"}
                      </span>
                      <span className="ml-2 tabular-nums text-xs text-ink-500">{b.score_total ?? "—"}</span>
                    </Td>
                    <Td>
                      {b.referring_mlo_name ? (
                        <span className="text-navy">{b.referring_mlo_name}</span>
                      ) : (
                        <span className="text-[11px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300">Unassigned</span>
                      )}
                    </Td>
                    <Td>
                      <OnboardingCell
                        row={onboarding[b.email]}
                        email={b.email}
                        busy={obBusy === b.email}
                        onSendLink={() => sendLink(b.email)}
                        onNudge={() => nudgePartner(b.email)}
                      />
                    </Td>
                    <Td className="tabular-nums">{b.packets_sent}</Td>
                    <Td className="tabular-nums">{b.reports_submitted}</Td>
                    <Td className="text-ink-500 whitespace-nowrap">{fmtDate(b.last_activity)}</Td>
                    <Td>
                      <button
                        data-testid={`admin-bdm-timeline-${b.email}`}
                        onClick={() => setActiveBdm(b)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-teal hover:text-navy transition-colors"
                      >
                        <Clock size={13} /> Timeline
                      </button>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {activeBdm && <BDMActivityDrawer bdm={activeBdm} onClose={() => setActiveBdm(null)} />}
      {showIssue && (
        <BDMBulkIssueModal
          recipients={selected}
          onClose={() => setShowIssue(false)}
          onSent={() => {
            setShowIssue(false);
            setSelected([]);
          }}
        />
      )}
    </>
  );
}

function OnboardingCell({ row, email, busy, onSendLink, onNudge }) {
  if (!row) {
    return (
      <button
        onClick={onSendLink}
        disabled={busy}
        data-testid={`admin-bdm-onboarding-send-${email}`}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-navy text-white px-2.5 py-1.5 rounded-full hover:bg-navy-light disabled:opacity-50 transition-colors"
        title="Create and email this partner's personal checklist link"
      >
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Send checklist
      </button>
    );
  }
  const p = row.progress;
  return (
    <div className="min-w-[190px]" data-testid={`admin-bdm-onboarding-${email}`}>
      <div className="flex items-center gap-2">
        <span className="w-16 h-1.5 rounded-full bg-linen overflow-hidden shrink-0">
          <span
            className="block h-full rounded-full"
            style={{ width: `${p.percent}%`, background: p.complete ? "#1F8F8B" : "#A6D24A" }}
          />
        </span>
        <span className="text-xs tabular-nums text-navy font-medium">{p.done}/{p.total}</span>
        {p.complete && <CheckCircle2 size={13} className="text-teal" />}
      </div>
      <div className="mt-0.5 text-[11px] text-ink-500 leading-snug">
        {p.complete ? "Finished their first week" : p.started ? `Stuck on: ${p.stuck_label}` : "Not started"}
      </div>
      {!p.complete && (
        <button
          onClick={onNudge}
          disabled={busy}
          data-testid={`admin-bdm-onboarding-nudge-${email}`}
          className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-teal hover:text-navy disabled:opacity-50 transition-colors"
        >
          {busy ? <Loader2 size={10} className="animate-spin" /> : <BellRing size={11} />} Nudge this partner
        </button>
      )}
    </div>
  );
}

const ACTIVITY_ICON = {  application: ClipboardList,
  review: CheckCircle2,
  assignment: UserCheck,
  packet: FolderOpen,
  report: FileText,
};
const ACTIVITY_COLOR = {
  application: "bg-teal/15 text-teal",
  review: "bg-lime-soft text-navy",
  assignment: "bg-navy/10 text-navy",
  packet: "bg-amber-100 text-amber-700",
  report: "bg-linen text-ink-600",
};

function BDMActivityDrawer({ bdm, onClose }) {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    api
      .get(`/admin/bdms/${encodeURIComponent(bdm.email)}/activity`)
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]));
  }, [bdm.email]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-bdm-drawer">
      <div className="absolute inset-0 bg-navy-deep/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ x: 60, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        className="relative w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto"
      >
        <div className="sticky top-0 bg-navy text-white px-6 py-5 flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-lime">Partner activity</div>
            <h3 className="mt-0.5 font-heading text-lg">{bdm.full_name}</h3>
            <div className="mt-1 flex items-center gap-2 text-xs text-white/70">
              <Mail size={12} /> {bdm.email}
            </div>
          </div>
          <button onClick={onClose} data-testid="admin-bdm-drawer-close" className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5">
          <div className="flex flex-wrap gap-2 text-xs">
            <span className={`inline-block uppercase tracking-widest rounded-full px-2 py-0.5 ${TIER_STYLE[bdm.classification] || TIER_STYLE.emerging}`}>
              {bdm.classification} · {bdm.score_total}
            </span>
            {bdm.referring_mlo_name && (
              <span className="rounded-full px-2 py-0.5 bg-linen border border-linen-border text-ink-600">
                MLO: {bdm.referring_mlo_name}
              </span>
            )}
            {bdm.mobile_phone && (
              <span className="rounded-full px-2 py-0.5 bg-linen border border-linen-border text-ink-600">{bdm.mobile_phone}</span>
            )}
          </div>

          {events === null ? (
            <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-teal" size={22} /></div>
          ) : events.length === 0 ? (
            <p className="mt-8 text-sm text-ink-400" data-testid="admin-bdm-timeline-empty">No activity recorded yet.</p>
          ) : (
            <ol className="mt-6 relative border-l border-linen-border ml-4 space-y-6" data-testid="admin-bdm-timeline">
              {events.map((ev, i) => {
                const Icon = ACTIVITY_ICON[ev.type] || Clock;
                return (
                  <li key={i} className="relative pl-8">
                    <span className={`absolute -left-[15px] top-0 w-[30px] h-[30px] rounded-full flex items-center justify-center border-2 border-white ${ACTIVITY_COLOR[ev.type] || "bg-linen text-ink-600"}`}>
                      <Icon size={14} />
                    </span>
                    <div className="text-sm font-medium text-navy leading-snug">{ev.title}</div>
                    {ev.detail && <div className="text-xs text-ink-500 mt-0.5 leading-relaxed">{ev.detail}</div>}
                    <div className="text-[11px] text-ink-400 mt-1">{fmtDate(ev.at)}</div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function BDMBulkIssueModal({ recipients, onClose, onSent }) {
  const [subs, setSubs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [target, setTarget] = useState("");
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [excludedCount, setExcludedCount] = useState(0);

  useEffect(() => {
    const [kind, id] = (target || "").split("|");
    if (kind !== "sub" || !id) {
      setExcludedCount(0);
      return;
    }
    api
      .get(`/library/admin/subcategories/${id}/uncategorized-count`)
      .then((r) => setExcludedCount(r.data?.uncategorized_count || 0))
      .catch(() => setExcludedCount(0));
  }, [target]);

  useEffect(() => {
    Promise.all([api.get("/library/subcategories"), api.get("/library/folders")])
      .then(([s, f]) => {
        setSubs(s.data || []);
        setFolders(f.data || []);
      })
      .catch(() => toast.error("Couldn't load library structure"));
  }, []);

  const subName = (id) => subs.find((s) => s.id === id)?.name || "";

  const defaultSubject = useMemo(() => {
    const [kind, id] = (target || "").split("|");
    if (!id) return "";
    const item = kind === "folder" ? folders.find((f) => f.id === id) : subs.find((s) => s.id === id);
    if (!item) return "";
    return `Great Rate Check — ${item.share_name || item.name}`;
  }, [target, folders, subs]);

  useEffect(() => {
    setSubject(defaultSubject);
  }, [defaultSubject]);

  const send = async () => {
    if (!target) return;
    const [kind, id] = target.split("|");
    setSending(true);
    try {
      const url = kind === "folder"
        ? `/library/admin/folders/${id}/issue`
        : `/library/admin/subcategories/${id}/issue`;
      const { data } = await api.post(url, { recipients, note, subject: subject.trim() });
      setResult(data);
      toast.success(`Sent to ${data.sent_to.length} partner${data.sent_to.length !== 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed — please retry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="admin-bulk-issue-modal">
      <div className="absolute inset-0 bg-navy-deep/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="bg-navy text-white px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-lime">Bulk issue</div>
            <h3 className="mt-0.5 font-heading text-lg">Send assets to {recipients.length} partner{recipients.length !== 1 ? "s" : ""}</h3>
          </div>
          <button onClick={onClose} data-testid="admin-bulk-issue-close" className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
            <X size={18} />
          </button>
        </div>

        {result ? (
          <div className="px-6 py-6" data-testid="admin-bulk-issue-success">
            <div className="flex items-center gap-3 rounded-2xl bg-lime-soft border border-[#A6D24A] p-4">
              <CheckCircle2 className="text-teal shrink-0" size={22} />
              <div className="text-sm text-navy">
                Emailed to <strong>{result.sent_to.length}</strong> partner{result.sent_to.length !== 1 ? "s" : ""} ·{" "}
                {result.item_count} item{result.item_count !== 1 ? "s" : ""} in the bundle.
              </div>
            </div>
            <div className="mt-3 text-xs text-ink-500 break-all">
              Share link: <a href={result.share_url} target="_blank" rel="noreferrer" className="text-teal underline">{result.share_url}</a>
            </div>
            <button
              onClick={onSent}
              data-testid="admin-bulk-issue-done"
              className="mt-5 w-full rounded-full bg-navy hover:bg-navy-light text-white font-semibold py-3 transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="px-6 py-6 space-y-4">
            <div className="text-xs text-ink-500 leading-relaxed max-h-16 overflow-y-auto rounded-xl bg-linen px-3 py-2">
              {recipients.join(", ")}
            </div>
            <label className="block">
              <span className="text-sm font-medium text-navy">What to send</span>
              <select
                data-testid="admin-bulk-issue-target"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-linen-border p-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal/40"
              >
                <option value="">Select a folder or subcategory…</option>
                {folders.length > 0 && (
                  <optgroup label="Folders">
                    {folders.map((f) => (
                      <option key={f.id} value={`folder|${f.id}`}>
                        {subName(f.subcategory_id)} / {f.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {subs.length > 0 && (
                  <optgroup label="Entire subcategories">
                    {subs.map((s) => (
                      <option key={s.id} value={`sub|${s.id}`}>{s.category} — {s.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </label>
            {excludedCount > 0 && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="admin-bulk-issue-excluded-note">
                {excludedCount} uncategorized file{excludedCount !== 1 ? "s" : ""} won't be included — only files inside folders go out.
              </div>
            )}
            <label className="block">
              <span className="text-sm font-medium text-navy">Email subject</span>
              <input
                data-testid="admin-bulk-issue-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={defaultSubject || "Great Rate Check — Current Asset Packet"}
                className="mt-1.5 w-full rounded-xl border border-linen-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40"
              />
              <span className="mt-1 block text-[11px] text-ink-500">What lands in their inbox — defaults to the section name.</span>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-navy">Note (optional)</span>
              <textarea
                data-testid="admin-bulk-issue-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="A short message included in the email…"
                className="mt-1.5 w-full rounded-xl border border-linen-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40"
              />
            </label>
            <button
              onClick={send}
              disabled={!target || sending}
              data-testid="admin-bulk-issue-send"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy hover:bg-navy-light disabled:opacity-50 text-white font-semibold py-3 transition-colors"
            >
              {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
              Send to {recipients.length} partner{recipients.length !== 1 ? "s" : ""}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

/* ----------------------------- Brokerage Inquiries Panel ----------------------------- */
const INQUIRY_STATUSES = ["new", "contacted", "scheduled", "closed"];
const INQUIRY_STATUS_STYLE = {
  new: "bg-amber-100 text-amber-800 border border-amber-300",
  contacted: "bg-teal/15 text-teal border border-teal/40",
  scheduled: "bg-lime-soft text-navy-deep border border-[#A6D24A]",
  closed: "bg-linen text-ink-500 border border-linen-border",
};

function InquiriesPanel({ inquiries, q, onChanged }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [savingId, setSavingId] = useState(null);

  const statusOf = (i) => i.status || "new";

  const counts = useMemo(() => {
    const c = { all: inquiries.length };
    for (const s of INQUIRY_STATUSES) c[s] = inquiries.filter((i) => statusOf(i) === s).length;
    return c;
  }, [inquiries]);

  const filtered = useMemo(() => {
    let list = inquiries;
    if (statusFilter !== "all") list = list.filter((i) => statusOf(i) === statusFilter);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((i) =>
        [i.full_name, i.brokerage_name, i.email, i.phone, i.city_state, i.message]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(s))
      );
    }
    return list;
  }, [inquiries, statusFilter, q]);

  const setStatus = async (inquiry, status) => {
    setSavingId(inquiry.id);
    try {
      const { data } = await api.patch(`/admin/brokerage-inquiries/${inquiry.id}`, { status });
      onChanged(inquiry.id, { status: data.status, updated_at: data.updated_at });
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Couldn't update status — please retry.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="admin-inquiries-status-filter">
        <span className="text-xs uppercase tracking-widest text-ink-500 mr-1">Status:</span>
        {[["all", "All"], ...INQUIRY_STATUSES.map((s) => [s, s.charAt(0).toUpperCase() + s.slice(1)])].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            data-testid={`admin-inquiries-status-${key}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
              statusFilter === key
                ? (key === "new" && counts.new > 0 ? "bg-amber-500 text-white shadow" : "bg-navy text-white shadow")
                : "bg-white border border-linen-border text-ink-600 hover:border-teal"
            }`}
          >
            {label}
            <span className={`ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded-full ${statusFilter === key ? "bg-white/20" : "bg-linen"}`}>
              {counts[key] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-inquiries-table">
            <thead className="bg-linen text-left text-ink-500">
              <tr>
                <Th>Contact</Th>
                <Th>Brokerage</Th>
                <Th>Agents</Th>
                <Th>Message</Th>
                <Th>Received</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-linen-border">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-ink-400" data-testid="admin-inquiries-empty">
                    No brokerage inquiries yet. Submissions from the /partnerprogram brokerage track land here.
                  </td>
                </tr>
              ) : (
                filtered.map((i) => (
                  <tr key={i.id} className="hover:bg-linen/50 transition-colors align-top" data-testid={`admin-inquiry-row-${i.id}`}>
                    <Td>
                      <div className="font-medium text-navy">{i.full_name}</div>
                      <a href={`mailto:${i.email}`} className="text-xs text-teal hover:underline block">{i.email}</a>
                      <a href={`tel:${i.phone}`} className="text-xs text-ink-500 block">{i.phone}</a>
                    </Td>
                    <Td>
                      <div className="font-medium text-navy">{i.brokerage_name}</div>
                      {i.inquiry_type === "teamlead" && (
                        <span className="inline-block mt-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-teal text-white" data-testid={`admin-inquiry-teamlead-${i.id}`}>Team lead</span>
                      )}
                      <div className="text-xs text-ink-400">{i.city_state || "—"}</div>
                    </Td>
                    <Td className="tabular-nums whitespace-nowrap">{i.agent_count}</Td>
                    <Td>
                      {i.message ? (
                        <span className="block max-w-xs text-ink-600 text-[13px] leading-snug" title={i.message}>
                          {i.message.length > 110 ? `${i.message.slice(0, 110)}…` : i.message}
                        </span>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </Td>
                    <Td className="text-ink-500 whitespace-nowrap">{fmtDate(i.created_at)}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <span className={`inline-block text-[11px] uppercase tracking-widest rounded-full px-2 py-0.5 ${INQUIRY_STATUS_STYLE[statusOf(i)]}`}>
                          {statusOf(i)}
                        </span>
                        {savingId === i.id ? (
                          <Loader2 size={14} className="animate-spin text-teal" />
                        ) : (
                          <select
                            value={statusOf(i)}
                            onChange={(e) => setStatus(i, e.target.value)}
                            data-testid={`admin-inquiry-status-${i.id}`}
                            className="text-xs py-1 px-2 rounded-full bg-white border border-linen-border focus:outline-none focus:ring-2 focus:ring-teal/40"
                          >
                            {INQUIRY_STATUSES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ----------------------------- Links Panel ----------------------------- */


function copyToClipboard(text) {
  return navigator.clipboard
    .writeText(text)
    .then(() => toast.success("Link copied"))
    .catch(() => toast.error("Couldn't copy — select and copy manually."));
}

function LinkRow({ label, url, og, sub, onEmail }) {
  return (
    <div className="flex items-center gap-4 px-4 sm:px-5 py-3 hover:bg-linen/50 transition-colors" data-testid={`admin-link-row-${url.split("/").slice(3).join("-") || "home"}`}>
      <div className="hidden sm:block w-[84px] h-[44px] rounded-lg overflow-hidden bg-linen border border-linen-border shrink-0">
        {og ? (
          <img src={og} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-ink-300"><Link2 size={16} /></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-navy truncate">{label}</div>
        <div className="text-xs text-teal truncate">{url}</div>
        {sub && <div className="text-[11px] text-ink-400 truncate">{sub}</div>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          onClick={() => copyToClipboard(url)}
          data-testid={`admin-link-copy-${url.split("/").slice(3).join("-") || "home"}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium bg-navy text-white px-3 py-1.5 rounded-full hover:bg-navy-light transition-colors"
        >
          <Copy size={12} /> Copy
        </button>
        <button
          onClick={onEmail}
          data-testid={`admin-link-email-${url.split("/").slice(3).join("-") || "home"}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-linen-border text-ink-600 px-3 py-1.5 rounded-full hover:border-teal transition-colors"
        >
          <Mail size={12} /> Email
        </button>
        <a
          href={`${API}/qr.png?data=${encodeURIComponent(url)}&download=1`}
          data-testid={`admin-link-qr-${url.split("/").slice(3).join("-") || "home"}`}
          className="p-1.5 rounded-full text-ink-400 hover:text-navy transition-colors"
          title="Download QR code"
        >
          <QrCode size={14} />
        </a>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="p-1.5 rounded-full text-ink-400 hover:text-teal transition-colors"
          title="Open"
        >
          <ExternalLink size={14} />
        </a>
      </div>
    </div>
  );
}

function LinksPanel({ mlos, q }) {
  const [emailTarget, setEmailTarget] = useState(null); // {label, url}
  const [bundleTarget, setBundleTarget] = useState(null); // {title, links:[{label,use,url}]}
  const origin = window.location.origin;
  const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;

  const groups = useMemo(() => {
    const s = (q || "").toLowerCase();
    const match = (label, url) => !s || label.toLowerCase().includes(s) || url.toLowerCase().includes(s);
    const core = {};
    for (const l of CORE_LINKS) {
      const url = `${origin}${l.path}`;
      if (!match(l.label, url)) continue;
      (core[l.group] = core[l.group] || []).push({ ...l, url });
    }
    for (const d of DOWNLOAD_LINKS) {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api${d.apiPath}`;
      if (!match(d.label, url)) continue;
      (core.Downloads = core.Downloads || []).push({ ...d, url, og: null });
    }
    const mloKits = [];
    for (const m of mlos) {
      if (m.demo) continue; // sample page lives in the Walkthrough group instead
      const page = `${origin}/${m.slug}`;
      const ref = `${origin}/${m.slug}/partnerapply`;
      if (!match(m.name || m.slug, page)) continue;
      mloKits.push({
        mlo: m,
        links: [
          { label: `${m.name} — branded landing page`, use: `Introduce ${m.name} — their branded consumer rate-check page.`, url: page, og: `${API_ORIGIN}/api/og/mlo/${m.slug}.jpg`, sub: m.nmls ? `NMLS #${m.nmls}` : null },
          { label: `${m.name} — borrower apply page`, use: `Send a borrower straight to ${m.name}'s application.`, url: `${origin}/${m.slug}/apply`, og: `${API_ORIGIN}/api/og/mlo/${m.slug}.jpg`, sub: m.apply_url ? null : "⚠ No application link set — buttons show \"Call\" instead" },
          { label: `${m.name} — partner referral apply link`, use: `For agents joining under ${m.name} — applications auto-tag to them.`, url: ref, og: "/og-partnerapply.jpg" },
        ],
      });
    }
    return { core, mloKits };
  }, [mlos, q, origin, API_ORIGIN]);

  const emailSection = (groupName, links) => {
    const key = toolkitKeyForGroup(groupName);
    setBundleTarget({
      title: `${groupName} toolkit`,
      toolkit: key,
      toolkitUrl: key ? `${origin}/toolkit/${key}` : "",
      links: links.map((l) => ({ label: l.label, use: l.use || "", url: l.url })),
    });
  };

  const emailKit = (kit) =>
    setBundleTarget({
      title: `${kit.mlo.name} — intro kit`,
      toolkit: "mlo-pages",
      toolkitUrl: `${origin}/toolkit/mlo-pages`,
      links: kit.links.map((l) => ({ label: l.label, use: l.use || "", url: l.url })),
    });

  return (
    <div className="space-y-6" data-testid="admin-links-panel">
      <ToolkitIntrosEditor />
      {["Recruiting", "Walkthrough", "Applications", "Consumer", "Partners", "Downloads"].map((g) =>
        groups.core[g]?.length ? (
          <div key={g} className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
            <div className="px-5 py-3 bg-linen border-b border-linen-border flex items-center justify-between gap-3">
              <span className="text-xs uppercase tracking-widest text-ink-500">{g}</span>
              <div className="flex items-center gap-2">
                {toolkitKeyForGroup(g) ? (
                  <a
                    href={`/toolkit/${toolkitKeyForGroup(g)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`admin-toolkit-page-${g.toLowerCase()}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium border border-linen-border bg-white px-3 py-1.5 rounded-full text-ink-600 hover:border-teal hover:text-navy transition-colors"
                  >
                    <ExternalLink size={11} /> Reference page
                  </a>
                ) : null}
                <button
                  onClick={() => emailSection(g, groups.core[g])}
                  data-testid={`admin-links-bundle-${g.toLowerCase()}`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium bg-teal text-white px-3 py-1.5 rounded-full hover:bg-teal-hover transition-colors"
                >
                  <Mail size={12} /> Email {g.toLowerCase()} toolkit
                </button>
              </div>
            </div>
            <div className="divide-y divide-linen-border">
              {groups.core[g].map((l) => (
                <LinkRow key={l.url} label={l.label} url={l.url} og={l.og} sub={l.use} onEmail={() => setEmailTarget({ label: l.label, url: l.url })} />
              ))}
            </div>
          </div>
        ) : null
      )}
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
        <div className="px-5 py-3 bg-linen border-b border-linen-border text-xs uppercase tracking-widest text-ink-500">
          MLO pages ({groups.mloKits.length})
        </div>
        <div data-testid="admin-links-mlo-list">
          {groups.mloKits.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-ink-400">No MLO pages match.</div>
          ) : (
            groups.mloKits.map((kit) => (
              <div key={kit.mlo.slug} className="border-b border-linen-border last:border-b-0" data-testid={`admin-mlo-kit-${kit.mlo.slug}`}>
                <div className="px-5 py-2.5 bg-linen/60 flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-navy">{kit.mlo.name}</span>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {[
                      ["openhouse", "Open house card"],
                      ["checkup", "Checkup postcard"],
                      ["closinggift", "Closing-gift card"],
                    ].map(([piece, plabel]) => (
                      <a
                        key={piece}
                        href={`${API_ORIGIN}/api/mlos/${kit.mlo.slug}/print/${piece}.pdf`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`admin-mlo-print-${piece}-${kit.mlo.slug}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-linen-border text-ink-600 px-3 py-1.5 rounded-full hover:border-teal transition-colors"
                      >
                        <QrCode size={12} /> {plabel}
                      </a>
                    ))}
                    <button
                      onClick={() => emailKit(kit)}
                      data-testid={`admin-mlo-kit-send-${kit.mlo.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium bg-navy text-white px-3 py-1.5 rounded-full hover:bg-navy-light transition-colors"
                    >
                      <Send size={12} /> Email intro kit
                    </button>
                  </div>
                </div>
                <div className="divide-y divide-linen-border">
                  {kit.links.map((l) => (
                    <LinkRow key={l.url} label={l.label} url={l.url} og={l.og} sub={l.use} onEmail={() => setEmailTarget({ label: l.label, url: l.url })} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {emailTarget && <SendLinkModal target={emailTarget} onClose={() => setEmailTarget(null)} />}
      {bundleTarget && <SendBundleModal target={bundleTarget} onClose={() => setBundleTarget(null)} />}
    </div>
  );
}

function SendLinkModal({ target, onClose }) {
  const [recipients, setRecipients] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    const list = recipients.split(/[,;\s]+/).map((r) => r.trim()).filter(Boolean);
    if (!list.length) {
      toast.error("Add at least one recipient email");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/send-link", {
        url: target.url,
        title: target.label,
        recipients: list,
        note,
      });
      toast.success(`Sent to ${data.sent_to.length} recipient${data.sent_to.length !== 1 ? "s" : ""}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed — please retry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="admin-send-link-modal">
      <div className="absolute inset-0 bg-navy-deep/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="bg-navy text-white px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-lime">Email a link</div>
            <h3 className="mt-0.5 font-heading text-base leading-snug">{target.label}</h3>
          </div>
          <button onClick={onClose} data-testid="admin-send-link-close" className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="text-xs text-teal break-all rounded-xl bg-linen px-3 py-2">{target.url}</div>
          <label className="block">
            <span className="text-sm font-medium text-navy">Recipients</span>
            <input
              data-testid="admin-send-link-recipients"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="name@example.com, name2@example.com"
              className="mt-1.5 w-full rounded-xl border border-linen-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-navy">Note (optional)</span>
            <textarea
              data-testid="admin-send-link-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="A short personal message…"
              className="mt-1.5 w-full rounded-xl border border-linen-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40"
            />
          </label>
          <button
            onClick={send}
            disabled={sending}
            data-testid="admin-send-link-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy hover:bg-navy-light disabled:opacity-50 text-white font-semibold py-3 transition-colors"
          >
            {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={15} />}
            Send link
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function ToolkitIntrosEditor() {
  const [open, setOpen] = useState(false);
  const [toolkits, setToolkits] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || toolkits) return;
    api.get("/toolkits").then((r) => setToolkits(r.data || {})).catch(() => setToolkits({}));
  }, [open, toolkits]);

  const setField = (key, field, value) =>
    setToolkits((t) => ({ ...t, [key]: { ...t[key], [field]: value } }));

  const setStep = (key, i, value) =>
    setToolkits((t) => {
      const steps = [...(t[key].steps || [])];
      steps[i] = value;
      return { ...t, [key]: { ...t[key], steps } };
    });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/toolkits", { toolkits });
      setToolkits(data);
      toast.success("Toolkit intros saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden" data-testid="admin-toolkit-intros">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="admin-toolkit-intros-toggle"
        className="w-full px-5 py-3.5 flex items-center justify-between gap-3 text-left hover:bg-linen/60 transition-colors"
      >
        <div>
          <p className="text-sm font-medium text-navy">Toolkit intros</p>
          <p className="text-xs text-ink-500 mt-0.5">
            The "Start here" text and how-to steps that ship with every toolkit email and reference page.
          </p>
        </div>
        <span className="text-xs text-teal shrink-0">{open ? "Hide" : "Edit"}</span>
      </button>
      {open && (
        <div className="border-t border-linen-border p-5 space-y-5">
          {toolkits === null ? (
            <p className="text-sm text-ink-500">Loading…</p>
          ) : (
            <>
              {Object.values(toolkits).map((tk) => (
                <div key={tk.key} className="rounded-2xl border border-linen-border p-4">
                  <p className="text-xs uppercase tracking-widest text-teal">{tk.label}</p>
                  <textarea
                    value={tk.start_here}
                    onChange={(e) => setField(tk.key, "start_here", e.target.value)}
                    rows={3}
                    data-testid={`admin-toolkit-start-${tk.key}`}
                    className="mt-2 w-full rounded-xl border border-linen-border p-3 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-teal/40"
                  />
                  <div className="mt-2 space-y-2">
                    {(tk.steps || []).map((st, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs text-ink-400 w-4 shrink-0">{i + 1}.</span>
                        <input
                          value={st}
                          onChange={(e) => setStep(tk.key, i, e.target.value)}
                          data-testid={`admin-toolkit-step-${tk.key}-${i}`}
                          className="flex-1 rounded-xl border border-linen-border p-2.5 text-[13px] text-navy focus:outline-none focus:ring-2 focus:ring-teal/40"
                        />
                      </div>
                    ))}
                  </div>
                  <a
                    href={`/toolkit/${tk.key}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-mono text-teal hover:underline"
                  >
                    /toolkit/{tk.key} <ExternalLink size={10} />
                  </a>
                </div>
              ))}
              <button
                onClick={save}
                disabled={saving}
                data-testid="admin-toolkit-intros-save"
                className="inline-flex items-center gap-2 rounded-full bg-navy hover:bg-navy-light text-white text-sm font-semibold px-6 py-2.5 disabled:opacity-60"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : null}
                Save intros
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SendBundleModal({ target, onClose }) {
  const [recipients, setRecipients] = useState("");
  const [note, setNote] = useState("");
  const [startHere, setStartHere] = useState("");
  const [steps, setSteps] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!target.toolkit) return;
    api
      .get("/toolkits")
      .then((r) => {
        const tk = r.data?.[target.toolkit];
        if (tk) {
          setStartHere(tk.start_here || "");
          setSteps(tk.steps || []);
        }
      })
      .catch(() => {});
  }, [target.toolkit]);

  const send = async () => {
    const list = recipients.split(/[,;\s]+/).map((r) => r.trim()).filter(Boolean);
    if (!list.length) {
      toast.error("Add at least one recipient email");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/send-link-bundle", {
        title: target.title,
        links: target.links,
        recipients: list,
        note,
        toolkit: target.toolkit || null,
        start_here: startHere,
        toolkit_url: target.toolkitUrl || "",
      });
      toast.success(`Toolkit sent to ${data.sent_to.length} recipient${data.sent_to.length !== 1 ? "s" : ""}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed — please retry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="admin-send-bundle-modal">
      <div className="absolute inset-0 bg-navy-deep/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-navy text-white px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-lime">Email a link toolkit</div>
            <h3 className="mt-0.5 font-heading text-lg">{target.title}</h3>
          </div>
          <button onClick={onClose} data-testid="admin-send-bundle-close" className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="rounded-2xl border border-linen-border divide-y divide-linen-border" data-testid="admin-send-bundle-preview">
            {target.links.map((l) => (
              <div key={l.url} className="px-4 py-2.5">
                <div className="text-sm font-medium text-navy">{l.label}</div>
                {l.use && <div className="text-xs text-ink-500 mt-0.5">{l.use}</div>}
                <div className="text-[11px] text-teal break-all mt-0.5 font-mono">{l.url}</div>
              </div>
            ))}
          </div>
          <label className="block">
            <span className="text-sm font-medium text-navy">Recipients</span>
            <input
              data-testid="admin-send-bundle-recipients"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="teammate@example.com, teammate2@example.com"
              className="mt-1.5 w-full rounded-xl border border-linen-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-navy">"Start here" intro</span>
            <textarea
              data-testid="admin-send-bundle-start-here"
              value={startHere}
              onChange={(e) => setStartHere(e.target.value)}
              rows={3}
              placeholder="What this toolkit is for and who should use it…"
              className="mt-1.5 w-full rounded-xl border border-linen-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40"
            />
            <span className="mt-1 block text-[11px] text-ink-500">
              Sits above the links. Edit for this send only — change the saved default in "Toolkit intros".
            </span>
          </label>
          {steps.length > 0 && (
            <div className="rounded-2xl bg-linen border border-linen-border p-3.5" data-testid="admin-send-bundle-steps">
              <p className="text-[10px] uppercase tracking-widest text-ink-500 font-semibold">Also included below the links</p>
              <ol className="mt-1.5 space-y-1 text-[12px] text-ink-600 leading-relaxed list-decimal list-inside">
                {steps.map((st, i) => <li key={i}>{st}</li>)}
              </ol>
              <p className="mt-2 text-[11px] text-ink-500">
                Plus the hand-off note and a link to {target.toolkitUrl || "the reference page"}.
              </p>
            </div>
          )}
          <label className="block">
            <span className="text-sm font-medium text-navy">Note (optional)</span>
            <textarea
              data-testid="admin-send-bundle-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="A short message for your team…"
              className="mt-1.5 w-full rounded-xl border border-linen-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40"
            />
          </label>
          <button
            onClick={send}
            disabled={sending}
            data-testid="admin-send-bundle-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy hover:bg-navy-light disabled:opacity-50 text-white font-semibold py-3 transition-colors"
          >
            {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={15} />}
            Send {target.links.length} link{target.links.length !== 1 ? "s" : ""}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/* ----------------------------- Send History Panel ----------------------------- */
const SEND_TYPE_STYLE = {
  packet: "bg-navy text-white",
  folder: "bg-teal/15 text-teal border border-teal/40",
  toolkit: "bg-lime-soft text-navy-deep border border-[#A6D24A]",
  link: "bg-linen text-ink-600 border border-linen-border",
};
const SEND_TYPE_LABEL = { packet: "Packet", folder: "Folder", toolkit: "Toolkit", link: "Link" };

function SendHistoryPanel({ q }) {
  const [rows, setRows] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [clicksFor, setClicksFor] = useState(null);

  useEffect(() => {
    api
      .get("/admin/send-history")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, []);

  const counts = useMemo(() => {
    const c = { all: rows?.length || 0 };
    for (const t of Object.keys(SEND_TYPE_LABEL)) c[t] = (rows || []).filter((r) => r.type === t).length;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows || [];
    if (typeFilter !== "all") list = list.filter((r) => r.type === typeFilter);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((r) =>
        [r.title, r.sent_by, r.note, ...(r.recipients || [])]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(s))
      );
    }
    return list;
  }, [rows, typeFilter, q]);

  if (rows === null) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="animate-spin text-teal" size={24} />
      </div>
    );
  }

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="admin-sends-filter">
        <span className="text-xs uppercase tracking-widest text-ink-500 mr-1">Type:</span>
        {[["all", "All"], ...Object.entries(SEND_TYPE_LABEL)].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTypeFilter(key)}
            data-testid={`admin-sends-type-${key}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
              typeFilter === key ? "bg-navy text-white shadow" : "bg-white border border-linen-border text-ink-600 hover:border-teal"
            }`}
          >
            {label}
            <span className={`ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded-full ${typeFilter === key ? "bg-white/20" : "bg-linen"}`}>
              {counts[key] ?? 0}
            </span>
          </button>
        ))}
      </div>
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-sends-table">
            <thead className="bg-linen text-left text-ink-500">
              <tr>
                <Th>Sent</Th>
                <Th>Type</Th>
                <Th>What</Th>
                <Th>Recipients</Th>
                <Th>By</Th>
                <Th>Opens</Th>
                <Th>Clicks</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-linen-border">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-ink-400" data-testid="admin-sends-empty">
                    No sends yet. Issued packets, emailed links, and toolkits will appear here.
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => (
                  <tr key={i} className="hover:bg-linen/50 transition-colors align-top" data-testid={`admin-send-row-${i}`}>
                    <Td className="whitespace-nowrap text-ink-500">{fmtDate(r.created_at)}</Td>
                    <Td>
                      <span className={`inline-block text-[11px] uppercase tracking-widest rounded-full px-2 py-0.5 ${SEND_TYPE_STYLE[r.type] || SEND_TYPE_STYLE.link}`}>
                        {SEND_TYPE_LABEL[r.type] || r.type}
                      </span>
                    </Td>
                    <Td>
                      <div className="font-medium text-navy">{r.title}</div>
                      <div className="text-xs text-ink-400">
                        {r.item_count} item{r.item_count !== 1 ? "s" : ""}
                        {r.note ? <span title={r.note}> · “{r.note.length > 40 ? `${r.note.slice(0, 40)}…` : r.note}”</span> : null}
                      </div>
                    </Td>
                    <Td>
                      <span className="block max-w-[220px] text-[13px] text-ink-600 leading-snug" title={(r.recipients || []).join(", ")}>
                        {(r.recipients || []).slice(0, 2).join(", ")}
                        {(r.recipients || []).length > 2 ? ` +${r.recipients.length - 2} more` : ""}
                      </span>
                    </Td>
                    <Td className="text-ink-600">{r.sent_by}</Td>
                    <Td>
                      {r.open_count === null ? (
                        <span className="text-ink-300">—</span>
                      ) : r.open_count > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-teal" title={`First: ${fmtDate(r.first_opened_at)} · Last: ${fmtDate(r.last_opened_at)}`}>
                          <Eye size={13} /> {r.open_count}× <span className="text-ink-400 font-normal">· {fmtDate(r.last_opened_at)}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-ink-400">Not yet</span>
                      )}
                    </Td>
                    <Td>
                      {!r.tracked ? (
                        <span className="text-ink-300" title="Sent before click tracking, or not a toolkit send">—</span>
                      ) : r.click_count > 0 ? (
                        <button
                          onClick={() => setClicksFor(r.send_id)}
                          data-testid={`admin-send-clicks-${i}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy bg-lime-soft border border-[#A6D24A] px-2.5 py-1 rounded-full hover:brightness-95 transition-all"
                          title={`Last click ${fmtDate(r.last_clicked_at)}`}
                        >
                          <MousePointerClick size={12} /> {r.click_count} · who?
                        </button>
                      ) : (
                        <button
                          onClick={() => setClicksFor(r.send_id)}
                          data-testid={`admin-send-clicks-${i}`}
                          className="text-xs text-ink-400 hover:text-teal transition-colors"
                        >
                          No clicks yet
                        </button>
                      )}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {clicksFor && <ToolkitClicksDrawer sendId={clicksFor} onClose={() => setClicksFor(null)} />}
    </>
  );
}

function ToolkitClicksDrawer({ sendId, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/link-sends/${sendId}/clicks`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [sendId]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-toolkit-clicks-drawer">
      <div className="absolute inset-0 bg-navy-deep/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ x: 40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        className="relative w-full max-w-lg bg-linen h-full overflow-y-auto shadow-2xl"
      >
        <div className="sticky top-0 bg-navy text-white px-6 py-5 flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-lime">Toolkit engagement</div>
            <h3 className="mt-0.5 font-heading text-lg">{data?.title || "Loading…"}</h3>
            {data && (
              <p className="mt-1 text-xs text-white/70">
                {data.unique_clickers} of {data.recipient_count} recipients clicked · {data.total_clicks} total click{data.total_clicks !== 1 ? "s" : ""}
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="admin-toolkit-clicks-close" className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
            <X size={18} />
          </button>
        </div>

        {data === null ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-teal" size={22} /></div>
        ) : data === false ? (
          <p className="p-6 text-sm text-ink-500">Couldn't load click data for this send.</p>
        ) : (
          <div className="p-5 space-y-5">
            <section>
              <div className="text-[11px] uppercase tracking-widest text-ink-500 font-semibold">Who clicked</div>
              <div className="mt-2 space-y-2" data-testid="admin-clicks-by-recipient">
                {data.recipients.map((r) => (
                  <div key={r.recipient} className={`rounded-2xl border p-4 ${r.total > 0 ? "bg-white border-linen-border" : "bg-white/50 border-dashed border-linen-border"}`}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-navy truncate">{r.recipient}</span>
                      {r.total > 0 ? (
                        <span className="shrink-0 text-[11px] font-semibold text-navy bg-lime-soft border border-[#A6D24A] rounded-full px-2 py-0.5">
                          {r.total} click{r.total !== 1 ? "s" : ""}
                        </span>
                      ) : (
                        <span className="shrink-0 text-[11px] text-ink-400">No clicks</span>
                      )}
                    </div>
                    {r.total > 0 && (
                      <ul className="mt-2.5 space-y-1.5">
                        {r.links.map((l) => (
                          <li key={l.url} className="flex items-start gap-2 text-[12px]">
                            <MousePointerClick size={12} className="mt-0.5 text-teal shrink-0" />
                            <span className="flex-1 min-w-0">
                              <span className="block text-navy font-medium truncate">{l.label || l.url}</span>
                              <span className="block text-ink-400">{l.count}× · last {fmtDate(l.last_clicked_at)}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </section>
            <section>
              <div className="text-[11px] uppercase tracking-widest text-ink-500 font-semibold">By link</div>
              <div className="mt-2 rounded-2xl bg-white border border-linen-border divide-y divide-linen-border" data-testid="admin-clicks-by-link">
                {data.links.map((l) => (
                  <div key={l.url} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[13px] font-medium text-navy">{l.label || l.url}</span>
                      <span className="shrink-0 text-xs tabular-nums text-ink-500">{l.count}</span>
                    </div>
                    <div className="text-[11px] text-teal break-all">{l.url}</div>
                    {l.clickers.length > 0 && (
                      <div className="mt-1 text-[11px] text-ink-500">Clicked by {l.clickers.join(", ")}</div>
                    )}
                  </div>
                ))}
              </div>
            </section>
            <p className="text-[11px] text-ink-400 leading-relaxed">
              Clicks are counted per recipient through a redirect on each link. Email opens aren't tracked —
              inbox image blocking makes them unreliable, so a click is the only signal worth acting on.
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
